import type { HelpContent } from "../types/help";

// ─────────────────────────────────────────────────────────────
// Central Help Content Registry
// Each key matches the internal page key used with handleNavigate().
// Content is permission-aware: items with a requiredPermission field
// are filtered at render time using the user's live permissions.
// This means the help system automatically adapts to any custom role
// a customer creates — no persona-specific logic required.
// ─────────────────────────────────────────────────────────────

export const HELP_CONTENT: HelpContent = {
	// ── DASHBOARD ────────────────────────────────────────────────
	dashboard: {
		pageTitle: "Dashboard",
		description:
			"Your central view of the company's current cash position, upcoming obligations, and forecast summary.",
		icon: "LayoutDashboard",
		iconColor: "bg-blue-500",
		functionality: {
			overview:
				"The Dashboard brings together the most important cash flow information for your role in one place. It shows your current cash balance, upcoming transactions, forecast totals, and any items that need your attention — so you can see at a glance whether the business is on track.",
			keyFeatures: [
				{
					label: "Current Cash Balance",
					detail:
						"Shows the live total cash across all bank accounts so you always know where the business stands today.",
					requiredPermission: "view_bank_accounts_card",
				},
				{
					label: "90-Day Forecast Chart",
					detail:
						"Displays a rolling 90-day cash projection with colour-coded zones to flag when cash may dip below safe levels.",
					requiredPermission: "view_90day_interactive_chart_card",
				},
				{
					label: "Overdue Invoices",
					detail:
						"Highlights invoices that are past their due date so you can prioritise chasing.",
					requiredPermission: "view_overdue_invoices_mini_card",
				},
				{
					label: "Upcoming Payments",
					detail:
						"Lists payments due in the near term so you can plan cash availability.",
					requiredPermission: "view_upcoming_payments_mini_card",
				},
				{
					label: "Uncategorised Items",
					detail:
						"Flags transactions that have not yet been assigned a category, so the forecast remains accurate.",
					requiredPermission: "view_uncategorized_items_card",
				},
				{
					label: "Upcoming Recurring Transactions",
					detail:
						"Shows scheduled recurring items — such as rent, payroll, and subscriptions — so nothing is missed.",
					requiredPermission: "view_upcoming_recurring_transactions_card",
				},
				{
					label: "Recent Transactions",
					detail:
						"Lists the latest transactions pulled in from connected data sources for a quick review.",
					requiredPermission: "view_recent_transactions_card",
				},
			],
			sectionsAndFields: [
				{
					name: "Mini Cards (top row)",
					description:
						"Small summary cards showing key totals — current cash, 30-day forecast, overdue invoices, and upcoming payments.",
				},
				{
					name: "Bank Accounts Card",
					description:
						"Breaks down the total cash balance by individual bank account.",
				},
				{
					name: "90-Day Chart",
					description:
						"An interactive line chart showing the projected cash balance day by day over the next 90 days.",
				},
				{
					name: "Recent Transactions",
					description:
						"A scrollable list of the most recent transactions with category, date, and amount.",
				},
				{
					name: "Recurring Transactions",
					description:
						"A list of upcoming scheduled payments and receipts based on recurring templates.",
				},
				{
					name: "Uncategorised Items",
					description:
						"Transactions that have not yet been categorised — these need attention to keep the forecast accurate.",
				},
			],
			useCases: [
				"Checking the current cash position first thing in the morning before starting work.",
				"Spotting overdue invoices that need chasing before the end of the week.",
				"Reviewing whether any upcoming recurring payments might cause a cash shortfall.",
				"Identifying uncategorised transactions so they do not skew the forecast.",
			],
		},
		userGuide: {
			gettingStarted:
				"The Dashboard loads automatically when you log in. Click **Dashboard** in the left-hand navigation at any time to return to it. The cards shown depend on the permissions assigned to your role.",
			steps: [
				{
					title: "How do I check the current cash balance?",
					items: [
						{ text: "Click **Dashboard** in the left-hand navigation." },
						{
							text: "Locate the Bank Accounts card — it shows the total cash across all connected accounts, broken down by account.",
						},
						{
							text: "Click any account row to view recent transactions for that account.",
							tip: "The current cash balance reflects live data imported from your connected data sources.",
						},
					],
					requiredPermission: "view_bank_accounts_card",
				},
				{
					title: "How do I view the 90-day forecast?",
					items: [
						{ text: "Click **Dashboard** in the left-hand navigation." },
						{ text: "Locate the 90-Day Forecast chart card." },
						{
							text: "Hover over any point on the chart to see the projected balance for that day.",
						},
						{
							text: "Click **View Full Forecast** to open the detailed Forecast Timeline screen.",
							tip: "Colour zones on the chart indicate healthy (green), caution (amber), and risk (red) cash levels.",
						},
					],
					requiredPermission: "view_90day_interactive_chart_card",
				},
				{
					title: "How do I see which transactions need categorising?",
					items: [
						{ text: "Click **Dashboard** in the left-hand navigation." },
						{
							text: "Locate the Uncategorised Items card — it shows how many transactions are waiting.",
						},
						{
							text: "Click the card or click **View All** to open the Transactions screen filtered to uncategorised items.",
						},
						{
							text: "Select a transaction, choose a category from the dropdown, then click **Save**.",
							note: "You need the Edit transaction categories permission to make changes.",
						},
					],
					requiredPermission: "view_uncategorized_items_card",
				},
				{
					title: "How do I check upcoming recurring transactions?",
					items: [
						{ text: "Click **Dashboard** in the left-hand navigation." },
						{ text: "Locate the Upcoming Recurring Transactions card." },
						{
							text: "Review the list — dates, amounts, and categories are shown for each scheduled item.",
						},
						{
							text: "Click **View All Recurring** to open the full Recurring Transactions screen.",
						},
					],
					requiredPermission: "view_upcoming_recurring_transactions_card",
				},
			],
			proTips: [
				"Categorise transactions daily rather than weekly — uncategorised items reduce forecast accuracy immediately.",
				"Use the 90-day chart colour zones as an early warning: if the line enters amber, investigate before it reaches red.",
				"Recurring transactions are the backbone of the forecast — keep them up to date to avoid surprises.",
			],
		},
		faq: [
			{
				question: "Why are some cards not showing on my dashboard?",
				answer:
					"Dashboard cards are controlled by your role permissions. If a card is not visible, your role has not been granted access to it. Contact your system administrator if you think a card should be available to you.",
			},
			{
				question: "What does the colour coding on the 90-day chart mean?",
				answer:
					"Green indicates a healthy cash level, amber indicates caution and that cash is approaching a lower threshold, and red indicates the projected balance is at risk. The thresholds are configured by your system administrator.",
				requiredPermission: "view_90day_interactive_chart_card",
			},
			{
				question: "How do I export data from the dashboard?",
				answer:
					"Individual cards may have an export option. For a full data export, navigate to the Transactions page or the Reports section.",
				requiredPermission: "export_data",
			},
			{
				question: "How often does the dashboard data refresh?",
				answer:
					"In Live Mode, data refreshes automatically based on your connected data sources' sync schedule. In Demo Mode, data is static and based on today's date.",
			},
		],
		relatedPages: [
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
			{ label: "Transactions", pageKey: "transactions" },
			{ label: "Recurring Transactions", pageKey: "recurring-transactions" },
		],
	},

	// ── TRANSACTIONS ─────────────────────────────────────────────
	transactions: {
		pageTitle: "Transactions",
		description:
			"View, search, and manage all individual transactions imported from your connected data sources.",
		icon: "ArrowLeftRight",
		iconColor: "bg-emerald-500",
		functionality: {
			overview:
				"The Transactions page shows every transaction that has been imported into the system. You can filter by date, category, account, and status, and — depending on your permissions — edit category assignments, adjust expected dates, and mark transactions as uncertain to reflect changes in confidence.",
			keyFeatures: [
				{
					label: "Full Transaction List",
					detail:
						"Browse all imported transactions with date, description, amount, category, and status at a glance.",
				},
				{
					label: "Filter and Search",
					detail:
						"Narrow the list by date range, transaction type, category, account, or keyword to find specific items quickly.",
				},
				{
					label: "Edit Categories",
					detail:
						"Change the category assigned to a transaction to ensure the forecast reflects the correct groupings.",
					requiredPermission: "edit_transaction_categories",
				},
				{
					label: "Export",
					detail:
						"Download the filtered transaction list as a CSV or Excel file for use in external reports.",
					requiredPermission: "export_data",
				},
			],
			sectionsAndFields: [
				{
					name: "Search Bar",
					description:
						"Type a keyword, reference number, or supplier name to filter the list instantly.",
				},
				{
					name: "Date Range Filter",
					description:
						"Set a start and end date to view transactions within a specific period.",
				},
				{
					name: "Type Filter",
					description:
						"Toggle between All, Incoming, and Outgoing to focus on receipts or payments.",
				},
				{
					name: "Category Filter",
					description:
						"Select one or more categories to show only transactions of that type.",
				},
				{
					name: "Transaction Row",
					description:
						"Each row shows the source, data source, reference, account name, description, transaction type, category, transaction date, due date, amount, outstanding balance, and status (Outstanding, Overdue, Settled, or Unknown).",
				},
				{
					name: "Transaction Detail",
					description:
						"Click a transaction row to open the detail panel, where you can view the full record and make edits.",
				},
				{
					name: "Confidence Badge",
					description:
						"A colour-coded label showing how certain the transaction is — Certain, Probable, Possible, or Unlikely.",
				},
			],
			useCases: [
				"Reviewing all transactions imported overnight from the accounting system to check for anything unusual.",
				"Finding a specific supplier payment and adjusting its expected date because the invoice terms have changed.",
				"Categorising a batch of new transactions so they appear correctly in the forecast.",
				"Exporting last month's transactions for use in an external reconciliation spreadsheet.",
				'Marking a large customer payment as "Possible" because the client has indicated they may pay late.',
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Transactions** in the left-hand navigation. All transactions are displayed. You can narrow down your search by using the filters at the top of the screen.",
			steps: [
				{
					title: "How do I find a specific transaction?",
					items: [
						{ text: "Click **Transactions** in the left-hand navigation." },
						{
							text: "Type a keyword into the **Search** bar — the list filters as you type.",
						},
						{
							text: "Use the **Date Range** filter to narrow results to a specific period.",
						},
						{
							text: "Use the **Type** filter to show only **Incoming** or **Outgoing** transactions if needed.",
							tip: "Combining the search bar with the date and type filters is the fastest way to locate a specific transaction.",
						},
					],
				},
				{
					title: "How do I change a transaction's category?",
					items: [
						{ text: "Click **Transactions** in the left-hand navigation." },
						{
							text: "Click the transaction row to open the Transaction Detail panel.",
							note: "The Transaction Detail panel automatically opens from the right-hand side of the screen. You can close the panel by clicking the arrow icon on the right-hand side middle of the screen.",
						},
						{
							text: "Click the **Category** field and select the correct category from the dropdown.",
						},
						{ text: "Click **Save**." },
					],
					requiredPermission: "edit_transaction_categories",
				},
				{
					title: "How do I export transactions?",
					items: [
						{ text: "Click **Transactions** in the left-hand navigation." },
						{
							text: "Apply any filters to narrow the list to the transactions you want to export.",
						},
						{ text: "Click the **Export** button above the transaction list." },
						{
							text: "Select **CSV** or **Excel**.",
							note: "The export includes only the transactions currently visible in the filtered list.",
						},
					],
					requiredPermission: "export_data",
				},
			],
			proTips: [
				"Categorise transactions as soon as they are imported — uncategorised items affect forecast accuracy immediately.",
				"Use the Confidence field for any transaction where timing or amount is not 100% confirmed. Even Probable is more accurate than treating everything as certain.",
				"If a large payment is consistently late from the same supplier, adjust the expected date on the recurring template rather than on individual transactions.",
				"Export a filtered list after month-end close as a record of all transactions in that period.",
			],
		},
		faq: [
			{
				question: "Can I delete a transaction?",
				answer:
					"No. Transactions imported from connected data sources cannot be deleted — they are a permanent record. You can adjust the category, date, or confidence level, but the original record is always preserved.",
			},
			{
				question: "How do I find all uncategorised transactions?",
				answer:
					'Use the Category filter and select "Uncategorised" to show only transactions that have not yet been assigned a category.',
			},

			{
				question: "How do I view incoming transactions only?",
				answer:
					'Use the Type filter at the top of the page and select "Incoming". The list will update to show only receipts and income.',
			},
			{
				question: "Can I edit multiple transactions at once?",
				answer:
					"Bulk editing is not currently available. Each transaction must be updated individually via the detail panel.",
			},
		],
		relatedPages: [
			{ label: "Recurring Transactions", pageKey: "recurring-transactions" },
			{ label: "Dashboard", pageKey: "dashboard" },
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
		],
	},

	// ── RECURRING TRANSACTIONS ────────────────────────────────────
	"recurring-transactions": {
		pageTitle: "Recurring Transactions",
		description:
			"Manage the templates that automatically generate regular payments and receipts in the forecast.",
		icon: "Repeat",
		iconColor: "bg-violet-500",
		functionality: {
			overview:
				"Recurring Transactions are templates that tell the system to expect a regular payment or receipt — such as rent, payroll, subscriptions, or loan repayments. Each template defines the amount, frequency, category, and start and end dates. The system uses these templates to populate the forecast with future transactions automatically.",
			keyFeatures: [
				{
					label: "Template List",
					detail:
						"View all active recurring transaction templates — both incoming and outgoing — with their schedule and next due date.",
				},
				{
					label: "Add New Template",
					detail:
						"Create a new recurring template for any regular payment or receipt that is not already captured by the connected data sources.",
					requiredPermission: "manage_recurring_transactions",
				},
				{
					label: "Edit Template",
					detail:
						"Update the amount, frequency, category, or dates on an existing template when the underlying arrangement changes.",
					requiredPermission: "manage_recurring_transactions",
				},
				{
					label: "Pause or Stop",
					detail:
						"Temporarily pause a recurring template or set an end date to stop it generating future transactions.",
					requiredPermission: "manage_recurring_transactions",
				},
				{
					label: "Category Assignment",
					detail:
						"Each template is assigned a category so its generated transactions appear in the correct forecast grouping.",
				},
			],
			sectionsAndFields: [
				{
					name: "Type Filter",
					description:
						"Toggle between All, Incoming, and Outgoing to focus on the type of recurring items you need.",
				},
				{
					name: "Template Row",
					description:
						"Each row shows the template name, frequency, amount, category, next due date, and current status.",
				},
				{
					name: "Template Name",
					description:
						'A descriptive label for the recurring item — for example, "Monthly Rent — Birmingham Office".',
				},
				{
					name: "Frequency",
					description:
						"How often the transaction repeats — weekly, monthly, quarterly, or annually.",
				},
				{
					name: "Amount",
					description:
						"The expected value of each occurrence. Can be a fixed amount or estimated.",
				},
				{
					name: "Category",
					description: "The forecast category this transaction contributes to.",
				},
				{
					name: "Next Due Date",
					description:
						"The date the next occurrence is expected based on the schedule.",
				},
				{
					name: "Status",
					description:
						"Active, Paused, or Ended — controls whether the template generates future transactions.",
				},
			],
			useCases: [
				"Adding a new office lease payment after signing a rental agreement.",
				"Updating payroll amounts after a salary review.",
				"Pausing a subscription payment while a service is temporarily not in use.",
				"Adding a new quarterly loan repayment that is not coming through the accounting system yet.",
				"Reviewing all outgoing recurring items to check none are out of date.",
			],
		},
		userGuide: {
			gettingStarted:
				'Navigate to Recurring Transactions from the sidebar. You will see a list of all current templates. To make changes, you need the "Edit recurring transactions" permission.',
			steps: [
				{
					title: "How do I add a new recurring transaction?",
					items: [
						{
							text: 'Click "Add Recurring Transaction" at the top of the page.',
						},
						{
							text: "Enter the template name, select Incoming or Outgoing, and set the amount.",
						},
						{
							text: "Choose the frequency — weekly, monthly, quarterly, or annually.",
						},
						{ text: "Set the start date and, if known, the end date." },
						{
							text: "Assign a category and click Save.",
							note: "The template will start generating forecast transactions from the start date you set.",
							tip: 'Use a clear, descriptive name — for example "Monthly Rent — Head Office" rather than just "Rent" — so it\'s easy to identify when reviewing the forecast.',
						},
					],
					requiredPermission: "manage_recurring_transactions",
				},
				{
					title: "How do I update an existing recurring template?",
					items: [
						{
							text: "Find the template in the list and click on it to open the detail view.",
						},
						{
							text: "Edit the fields you need to change — amount, frequency, category, or dates.",
						},
						{
							text: "Click Save to apply the changes.",
							note: "Changes apply to future occurrences only. Past transactions generated from this template are not affected.",
						},
					],
					requiredPermission: "manage_recurring_transactions",
				},
				{
					title: "How do I pause or stop a recurring transaction?",
					items: [
						{ text: "Find the template in the list and click on it." },
						{
							text: 'To pause temporarily: change the Status to "Paused" and click Save.',
						},
						{
							text: "To stop permanently: set an End Date to today or a past date and click Save.",
							note: "A paused template can be reactivated at any time. An ended template will no longer generate transactions from the end date onwards.",
						},
					],
					requiredPermission: "manage_recurring_transactions",
				},
			],
			proTips: [
				"Review recurring templates at least quarterly — amounts and schedules change over time and outdated templates silently distort the forecast.",
				"When a contract is renegotiated, update the template the same day rather than leaving it for month-end.",
				"Use the Outgoing filter and sort by amount to quickly spot your largest regular commitments.",
				"If a recurring payment is temporarily on hold (for example, during a dispute), pause the template rather than deleting it — you can reactivate it easily when it resumes.",
			],
		},
		faq: [
			{
				question:
					"What is the difference between a recurring transaction and a regular bank transaction?",
				answer:
					"A recurring transaction template is a forward-looking instruction — it tells the forecast to expect a regular payment or receipt. A regular bank transaction is a historical record of something that has already happened and been imported from the accounting system.",
			},
			{
				question:
					"Can I set a recurring transaction to end on a specific date?",
				answer:
					"Yes. When creating or editing a template, set the End Date field to the date the recurring item should stop. The system will not generate any forecast entries after that date.",
			},
			{
				question:
					"What happens if I change the amount on a recurring template?",
				answer:
					"The new amount applies to all future occurrences. Past transactions already generated from the template are not changed.",
				requiredPermission: "manage_recurring_transactions",
			},
			{
				question:
					"How do I know if a recurring template is included in the forecast?",
				answer:
					'Templates with a status of "Active" are included in the forecast. Paused or ended templates are excluded from future forecast periods.',
			},
			{
				question: "Can I create a recurring template for variable amounts?",
				answer:
					'You can enter an estimated amount. For transactions where the amount varies (such as utility bills), use your best estimate and update the template when the actual amount is confirmed. Alternatively, mark the generated transactions as "Probable" rather than "Certain" to reflect the variability.',
			},
		],
		relatedPages: [
			{ label: "Transactions", pageKey: "transactions" },
			{ label: "Dashboard", pageKey: "dashboard" },
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
		],
	},

	// ── FORECAST TIMELINE (90-DAY) ────────────────────────────────
	"forecast-90": {
		pageTitle: "Forecast Timeline",
		description:
			"A day-by-day view of the projected cash position over the next 90 days.",
		icon: "TrendingUp",
		iconColor: "bg-sky-500",
		functionality: {
			overview:
				"The Forecast Timeline shows the projected cash balance for every day over the next 90 days, based on current transactions, recurring templates, and any active scenarios. It is the most detailed view of short-term cash flow and is the primary tool for identifying when cash may be under pressure.",
			keyFeatures: [
				{
					label: "Day-by-Day Projection",
					detail:
						"See the expected cash balance for every individual day in the 90-day window, not just weekly or monthly totals.",
				},
				{
					label: "Colour-Coded Zones",
					detail:
						"The chart highlights healthy, caution, and risk zones so you can instantly spot where the balance is under pressure.",
				},
				{
					label: "Scenario Overlay",
					detail:
						"Apply a scenario to the chart to see how a change — such as a large payment or a delayed receipt — would affect the forecast.",
				},
				{
					label: "Transaction Drill-Down",
					detail:
						"Click any point on the chart to see which transactions contribute to the projected balance on that day. When multiple scenario lines are visible, a series picker appears so you can choose exactly which line to drill into.",
				},
				{
					label: "Incoming vs Outgoing",
					detail:
						"Toggle between a net balance view and a breakdown of incoming and outgoing cash flows.",
				},
			],
			sectionsAndFields: [
				{
					name: "Chart Area",
					description:
						"The main line chart showing projected daily cash balance over 90 days.",
				},
				{
					name: "Zone Indicators",
					description:
						"Colour bands on the chart — green (healthy), amber (caution), red (risk) — based on thresholds configured by your system administrator and optionally overridden in your personal preferences. The display mode (Off, Line only, or Full Bands) is also configurable per chart in My Preferences.",
				},
				{
					name: "Scenario Selector",
					description:
						"A dropdown to apply a saved scenario to the forecast and see the impact on the chart.",
				},
				{
					name: "View Toggle",
					description:
						"Switch between Net Balance, Incoming, and Outgoing views.",
				},
				{
					name: "Day Detail Panel",
					description:
						"Click any day on the chart to open a panel listing all expected transactions for that day.",
				},
				{
					name: "Summary Totals",
					description:
						"Key figures shown alongside the chart — opening balance, total in, total out, and closing balance for the period.",
				},
			],
			useCases: [
				"Checking whether payroll and rent landing in the same week will cause a cash shortfall.",
				"Identifying the lowest point in the cash balance over the next 3 months.",
				"Comparing the base forecast with a conservative scenario to understand downside risk.",
				"Drilling into a specific day to confirm which payments are expected to go out.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Forecasts** in the left-hand navigation then click **90-Day Timeline**. The chart loads automatically showing the base forecast from today's date.",
			steps: [
				{
					title: "How do I read the forecast chart?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **90-Day Timeline**.",
						},
						{
							text: "Review the line on the chart — it represents the projected cash balance at the end of each day.",
						},
						{
							text: "Note the colour zones: green indicates the balance is healthy, amber indicates it is approaching the caution threshold, red indicates it is in the risk zone.",
						},
						{
							text: "Hover over any point on the line to see the exact projected balance for that day.",
							tip: "The lowest point on the chart is the most important figure to monitor — this is when the business is most at risk of a cash shortfall.",
						},
					],
				},
				{
					title: "How do I drill into a specific day?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **90-Day Timeline**.",
						},
						{
							text: "Click anywhere on the chart for the day you want to inspect.",
						},
						{
							text: "If only the Base Forecast is visible, the Day Detail panel opens immediately.",
						},
						{
							text: "If one or more scenario overlays are selected, a series picker appears — showing each visible line with its colour, name, and projected value for that day. Click the row for the line you want to drill into.",
							tip: "The series picker makes it easy to drill into a specific line even when multiple scenario lines are clustered close together on the chart.",
						},
						{
							text: "The Day Detail panel opens showing all expected transactions for that day.",
							note: "Transactions shown here come from imported records, recurring templates, and any adjustments you have made.",
						},
					],
				},
				{
					title: "How do I apply a scenario to the forecast?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **90-Day Timeline**.",
						},
						{ text: "Find the **Scenario Selector** above the chart." },
						{ text: "Select a saved scenario from the dropdown." },
						{
							text: "The chart updates to show how the forecast changes under that scenario.",
							note: "Only scenarios you have access to will appear in the selector. You need the View scenarios permission.",
						},
					],
					requiredPermission: "view_scenarios",
				},
				{
					title:
						"How do I switch between Net Balance, Incoming, and Outgoing views?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **90-Day Timeline**.",
						},
						{ text: "Find the **View** toggle above the chart." },
						{
							text: "Click **Net Balance**, **Incoming**, or **Outgoing** to change the view.",
						},
					],
				},
			],
			proTips: [
				"Focus on the lowest point in the 90-day window — that is the date when cash management is most critical.",
				"If the line dips into amber, work backwards to identify which transactions are causing it and whether any dates can be adjusted.",
				"Use scenario overlays to test the impact of a delayed receipt before committing to new expenditure.",
				"Cross-reference the day detail panel with the Recurring Transactions screen to confirm scheduled items are up to date.",
			],
		},
		faq: [
			{
				question: "How is the forecast calculated?",
				answer:
					"The forecast starts from today's actual cash balance and adds or subtracts all expected transactions — imported records, recurring templates, and any manual adjustments — day by day. The result is the projected closing balance for each day.",
			},
			{
				question: "What does it mean if the line goes into the red zone?",
				answer:
					"The red zone indicates the projected cash balance has fallen below the risk threshold configured for your business. It does not mean the business will definitely run out of cash — it means the projection suggests the balance will be below the minimum safe level at that point. The threshold values are set by your system administrator as company defaults, and can be overridden in My Preferences. You can also choose to display just the threshold line or hide the zones entirely — see My Preferences, Forecast Alerts tab, Chart Health Zones.",
			},
			{
				question: "Can I print or export the forecast chart?",
				answer:
					"Use the Reports section to generate a formal cash flow forecast report. The Reports page provides formatted outputs suitable for sharing.",
				requiredPermission: "view_reports",
			},
			{
				question:
					"How do I know if the forecast includes all my recurring transactions?",
				answer:
					'Go to the Recurring Transactions page and check that all active templates have a status of "Active". Any paused or ended templates will not appear in the forecast.',
			},
			{
				question: "Why does the forecast only go 90 days ahead?",
				answer:
					"The 90-day view is focused on short-term operational cash management. For longer-term planning, use the 12-Month Forecast or the 3-Year Forecast.",
			},
		],
		relatedPages: [
			{ label: "Dashboard", pageKey: "dashboard" },
			{ label: "12-Month Forecast", pageKey: "forecast-12m" },
			{ label: "3-Year Forecast", pageKey: "forecast-3y" },
			{ label: "Scenarios", pageKey: "scenarios" },
		],
	},

	// ── 12-MONTH FORECAST ─────────────────────────────────────────
	"forecast-12m": {
		pageTitle: "12-Month Forecast",
		description:
			"A month-by-month view of the projected cash position across the next 12 months.",
		icon: "CalendarDays",
		iconColor: "bg-indigo-500",
		functionality: {
			overview:
				"The 12-Month Forecast provides a medium-term view of expected cash flow, broken down by month. It is used for planning purposes — understanding seasonal patterns, anticipating periods of low cash, and aligning expenditure decisions with projected income. The forecast is built from the same transaction data and recurring templates as the 90-day view, extended forward using historical patterns and known future commitments.",
			keyFeatures: [
				{
					label: "Monthly Totals",
					detail:
						"See total incoming, outgoing, and net cash for each of the next 12 months.",
				},
				{
					label: "Running Balance",
					detail:
						"Track how the cumulative cash balance is expected to change month by month.",
				},
				{
					label: "Scenario Comparison",
					detail:
						"Apply different scenarios to see how the 12-month picture changes under different assumptions.",
					requiredPermission: "view_scenarios",
				},
				{
					label: "Category Breakdown",
					detail:
						"Understand which categories are driving the largest cash movements in each month.",
				},
				{
					label: "Month Drill-Down",
					detail:
						"Click any point on the chart to drill into the transactions behind that month's balance. When multiple scenario lines are visible, a series picker lets you choose exactly which line to explore.",
				},
			],
			sectionsAndFields: [
				{
					name: "Monthly Chart",
					description:
						"A bar or line chart showing projected cash movements and balance by month.",
				},
				{
					name: "Monthly Summary Table",
					description:
						"A table listing each month with total incoming, total outgoing, net movement, and closing balance.",
				},
				{
					name: "Scenario Selector",
					description:
						"Apply a saved scenario to see the 12-month forecast under different conditions.",
				},
				{
					name: "Category Panel",
					description:
						"A breakdown of which categories contribute most to each month's cash movements.",
				},
			],
			useCases: [
				"Planning whether the business can afford a capital purchase in Q3 without affecting operational cash.",
				"Identifying months where cash is historically low so arrangements can be made in advance.",
				"Presenting a 12-month cash flow projection to a bank or investor.",
				"Checking whether the forecast aligns with the annual budget.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Forecasts** in the left-hand navigation then click **12-Month Forecast**. The chart and summary table load automatically showing the base forecast for the next 12 months.",
			steps: [
				{
					title: "How do I identify the lowest cash month?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **12-Month Forecast**.",
						},
						{
							text: "Review the Closing Balance column in the monthly summary table.",
						},
						{
							text: "The month with the lowest closing balance is the point of highest cash pressure.",
						},
						{
							text: "Click that month on the chart to see a breakdown of what is driving the low balance.",
							tip: "If the lowest month is more than six months away, you have time to plan — consider adjusting payment schedules or accelerating receipts.",
						},
					],
				},
				{
					title: "How do I drill into a specific month?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **12-Month Forecast**.",
						},
						{
							text: "Click anywhere on the chart for the month you want to inspect.",
						},
						{
							text: "If only the Base Forecast is visible, the drill-down opens immediately for that month.",
						},
						{
							text: "If one or more scenario overlays are selected, a series picker appears — showing each visible line with its colour, name, and value for that month. Click the row for the line you want to explore.",
							tip: "The series picker is especially useful when scenario lines converge — you can select the exact series without needing to click a precise point on the chart.",
						},
						{
							text: "A breakdown of the transactions driving that month's balance opens.",
						},
					],
				},
				{
					title: "How do I apply a scenario to the 12-month view?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **12-Month Forecast**.",
						},
						{ text: "Find the **Scenario Selector** above the chart." },
						{ text: "Select a scenario from the dropdown." },
						{
							text: "The chart and table update to reflect the scenario's adjustments.",
							note: "Only scenarios you have access to will appear in the list. You need the View scenarios permission.",
						},
					],
					requiredPermission: "view_scenarios",
				},
			],
			proTips: [
				"The 12-month view is most useful for planning conversations — use it to demonstrate cash implications before agreeing to new commitments.",
				"Compare the 12-month forecast against last year's actuals to validate whether the projection looks realistic.",
				"If the forecast shows a sustained downward trend, investigate which outgoing categories are growing fastest.",
			],
		},
		faq: [
			{
				question:
					"How is the 12-month forecast different from the 90-day forecast?",
				answer:
					"The 90-day forecast is day-by-day and based closely on known upcoming transactions. The 12-month forecast is month-by-month and extends further ahead, using a combination of known commitments and extrapolated patterns from historical data.",
			},
			{
				question: "How accurate is the 12-month forecast?",
				answer:
					"Accuracy decreases the further ahead the forecast goes. The first 3 months are generally reliable as they are based on known transactions. Months 4–12 are projections based on patterns and committed recurring items — they should be treated as indicative rather than precise.",
			},
			{
				question: "Can I export the 12-month forecast?",
				answer:
					"Yes. Use the Reports section to generate a formatted 12-month cash flow forecast report.",
				requiredPermission: "view_reports",
			},
		],
		relatedPages: [
			{ label: "Forecast Timeline (90-Day)", pageKey: "forecast-90" },
			{ label: "3-Year Forecast", pageKey: "forecast-3y" },
			{ label: "Scenarios", pageKey: "scenarios" },
		],
	},

	// ── 3-YEAR FORECAST ───────────────────────────────────────────
	"forecast-3y": {
		pageTitle: "3-Year Forecast",
		description:
			"A strategic long-term view of projected cash flow across the next three financial years.",
		icon: "BarChart3",
		iconColor: "bg-purple-500",
		functionality: {
			overview:
				"The 3-Year Forecast provides a high-level strategic view of expected cash flow across three financial years, broken down by quarter. It is designed to support longer-term planning decisions — capital investment, borrowing requirements, dividend planning, and growth strategy. The further projections are based on trend analysis from historical data and committed future items.",
			keyFeatures: [
				{
					label: "Quarterly Totals",
					detail:
						"See projected cash movements and balance for each quarter across three financial years.",
				},
				{
					label: "Year-on-Year Comparison",
					detail:
						"Compare projected performance across years to identify trends.",
				},
				{
					label: "Strategic Insights Panel",
					detail:
						"Narrative commentary on the key trends and drivers visible in the 3-year projection.",
				},
				{
					label: "Planning Recommendations",
					detail:
						"Suggested actions based on what the forecast indicates — such as timing of capital investment or refinancing.",
				},
				{
					label: "Scenario Modelling",
					detail:
						"Apply scenarios to test the long-term impact of strategic decisions.",
					requiredPermission: "view_scenarios",
				},
				{
					label: "Quarter/Year Drill-Down",
					detail:
						"Click any point on the chart to drill into the detail behind that quarter or year. When multiple scenario lines are visible, a series picker lets you select exactly which line to explore.",
				},
			],
			sectionsAndFields: [
				{
					name: "Quarterly Chart",
					description:
						"A chart showing projected cash by quarter across 3 years.",
				},
				{
					name: "Annual Summary",
					description:
						"Year totals for incoming, outgoing, net movement, and closing balance.",
				},
				{
					name: "Strategic Insights Panel",
					description:
						"Commentary on key trends, risks, and opportunities visible in the long-term forecast.",
				},
				{
					name: "Planning Recommendations",
					description:
						"Specific suggested actions to optimise the cash position over the 3-year horizon.",
				},
				{
					name: "Scenario Selector",
					description:
						"Apply a scenario to model the impact of strategic decisions on the long-term forecast.",
				},
			],
			useCases: [
				"Assessing whether the business can sustain a planned capital investment programme over three years.",
				"Presenting a long-term cash flow projection to a bank as part of a borrowing application.",
				"Identifying whether a strategic growth plan is cash-generative or cash-consuming over the planning horizon.",
				"Understanding the long-term impact of taking on a new long-term contract.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Forecasts** in the left-hand navigation then click **3-Year Forecast**. The chart loads automatically showing the base projection across the next three financial years.",
			steps: [
				{
					title: "How do I read the 3-year chart?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **3-Year Forecast**.",
						},
						{
							text: "Review the chart — each bar or data point represents one quarter.",
						},
						{
							text: "Look at the closing balance trend: rising indicates healthy cash generation, flat indicates a stable position, declining requires strategic attention.",
						},
						{
							text: "Review the Annual Summary below the chart for a year-level view.",
							tip: "A declining trend that starts in year 2 or 3 is a strategic planning signal — not an immediate crisis, but worth investigating now.",
						},
					],
				},
				{
					title: "How do I drill into a specific quarter or year?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **3-Year Forecast**.",
						},
						{
							text: "Click anywhere on the chart for the quarter or year you want to inspect.",
						},
						{
							text: "If only the Base Forecast is visible, the drill-down opens immediately.",
						},
						{
							text: "If one or more scenario overlays are selected, a series picker appears — showing each visible line with its colour, name, and value for that period. Click the row for the line you want to explore.",
							tip: "The series picker is especially helpful in the outer years where scenario lines can converge into a tight cluster on the chart.",
						},
						{
							text: "A summary breakdown of expected cash movements for that period opens.",
						},
					],
				},
				{
					title:
						"How do I view strategic insights and planning recommendations?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **3-Year Forecast**.",
						},
						{
							text: "Locate the **Strategic Insights** panel — it appears to the right of or below the main chart.",
						},
						{
							text: "Read the commentary for a plain-language summary of what the forecast indicates.",
						},
						{
							text: "Review the **Planning Recommendations** section for specific suggested actions.",
							note: "The Strategic Insights panel and Planning Recommendations are only visible to users with the relevant permissions.",
						},
					],
				},
				{
					title: "How do I apply a scenario to the 3-year view?",
					items: [
						{
							text: "Click **Forecasts** in the left-hand navigation then click **3-Year Forecast**.",
						},
						{ text: "Find the **Scenario Selector** above the chart." },
						{ text: "Select a scenario from the dropdown." },
						{
							text: "The chart updates to reflect the scenario's adjustments across the full three-year period.",
							note: "Only scenarios you have access to will appear in the list. You need the View scenarios permission.",
						},
					],
					requiredPermission: "view_scenarios",
				},
			],
			proTips: [
				"The 3-year forecast is a strategic planning tool, not an operational one — use it quarterly rather than daily.",
				"The further out the projection, the wider the range of uncertainty. Treat Year 3 as directional rather than precise.",
				"If you are presenting this to a board or external party, apply the Conservative scenario first to show the downside position.",
			],
		},
		faq: [
			{
				question: "How is the 3-year forecast built?",
				answer:
					"The first year is based closely on known transactions and committed recurring items. Years 2 and 3 are extrapolated from historical trends and adjusted for any known future changes — such as contracts ending or planned capital expenditure.",
			},
			{
				question: "Can I change the assumptions in the 3-year forecast?",
				answer:
					"Yes — use the Scenarios page to create a scenario that adjusts the assumptions, then apply it to the 3-year view using the Scenario Selector.",
				requiredPermission: "view_scenarios",
			},
			{
				question: "What does the Strategic Insights panel show?",
				answer:
					"The Strategic Insights panel provides a narrative summary of the key trends visible in the forecast — for example, whether cash generation is accelerating or decelerating, and what the main drivers are.",
			},
		],
		relatedPages: [
			{ label: "Forecast Timeline (90-Day)", pageKey: "forecast-90" },
			{ label: "12-Month Forecast", pageKey: "forecast-12m" },
			{ label: "Scenarios", pageKey: "scenarios" },
		],
	},

	// ── VARIANCE ANALYSIS ─────────────────────────────────────────
	variance: {
		pageTitle: "Variance Analysis",
		description:
			"Compare what was forecast against what actually happened, and understand the reasons for any differences.",
		icon: "BarChart3",
		iconColor: "bg-orange-500",
		functionality: {
			overview:
				"Variance Analysis compares the original forecast against actual transactions to show where the business performed differently from expectations. It identifies which categories produced the largest differences, whether those differences were favourable or unfavourable, and helps you understand the reasons behind them — so the forecast can be refined and future performance improved.",
			keyFeatures: [
				{
					label: "Category Breakdown",
					detail:
						"See variance figures for every category — both incoming and outgoing — so you can pinpoint exactly where the forecast was off.",
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					label: "Favourable vs Unfavourable",
					detail:
						"Each variance is flagged as favourable (better than forecast) or unfavourable (worse than forecast) so the most important items stand out immediately.",
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					label: "Period Selection",
					detail:
						"Switch between weekly, monthly, and quarterly views to analyse variance at the right level of detail for your purpose.",
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					label: "Drill-Down Detail",
					detail:
						"Click into any category to see the individual transactions that made up the actual figure, so you can trace variance back to source.",
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					label: "Data Source Provenance",
					detail:
						"Each category row shows which connected data source the actual figures came from, so every variance can be traced to its origin system.",
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					label: "Forecast Snapshots",
					detail:
						"Take a named snapshot of the current forecast at any point in time. Switch between snapshots in the selector to compare what was predicted at any historical point against what actually happened. The comparison is the engine behind meaningful variance analysis.",
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					label: "Archive Snapshots",
					detail:
						"Snapshots that are no longer needed can be archived to keep the selector tidy. Archiving is permanent — a snapshot cannot be recreated once removed — so a confirmation step is always required.",
					requiredPermission: "delete_forecast_snapshots",
				},
			],
			sectionsAndFields: [
				{
					name: "Period Selector",
					description:
						"Choose the time period to analyse — current week, current month, last month, or current quarter.",
				},
				{
					name: "Summary Bar",
					description:
						"A top-level summary showing total incoming variance, total outgoing variance, and the net cash variance for the selected period.",
				},
				{
					name: "Category Table",
					description:
						"A table listing each category with forecast amount, actual amount, variance value, and variance percentage.",
				},
				{
					name: "Variance Badge",
					description:
						"A green (favourable) or red (unfavourable) badge on each row indicating the direction of the variance.",
				},
				{
					name: "Detail Panel",
					description:
						"Click a category row to expand it and see the individual transactions that contributed to the actual figure.",
				},
				{
					name: "Source Chip",
					description:
						"A badge in the detail panel showing which data source the actual figures were imported from.",
				},
				{
					name: "Snapshot Selector",
					description:
						"A dropdown to switch between saved snapshots and the live forecast. Shows the snapshot date and creator. The selected snapshot feeds all summary cards and the Balance Reconciliation tab.",
				},
				{
					name: "Take Snapshot Button",
					description:
						"Camera icon in the page header. Opens a dialog to name the snapshot, add an optional description, and choose a forecast horizon (how far forward the snapshot projects). Requires the Manage Forecast Snapshots permission.",
				},
				{
					name: "Coverage Warning Banner",
					description:
						"An amber banner that appears when the selected snapshot's horizon is shorter than the current analysis period — warning that comparison values reflect partial data only.",
				},
				{
					name: "Context Banner",
					description:
						"A blue banner confirming which snapshot is active when viewing historical data instead of the live forecast.",
				},
			],
			useCases: [
				"Reviewing at month-end why the actual cash position differs from what was forecast.",
				"Identifying which categories consistently over-perform or under-perform the forecast so the model can be refined.",
				"Presenting variance commentary to a finance director or board — understanding not just the numbers but the reasons.",
				"Tracing a large unfavourable variance back to specific transactions to confirm the cause.",
				"Checking whether a supplier payment category is consistently running late compared to forecast.",
				'Comparing "what we predicted in January for March" against what actually happened in March — using a January snapshot as the forecast baseline.',
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Variance** in the sidebar. Select the period you want to analyse using the **Period Selector** at the top of the screen. To compare against a historical forecast, select a saved snapshot from the **Snapshot Selector**.",
			steps: [
				{
					title: "How do I read the variance table?",
					items: [
						{ text: "Click **Variance** in the sidebar." },
						{
							text: "Review the Category Table — each row represents a transaction category such as Invoice Receipts or Supplier Payments.",
						},
						{
							text: "Compare the Forecast and Actual columns to identify the variance.",
						},
						{
							text: "Review the Variance Badge on each row — green indicates favourable, red indicates unfavourable.",
							note: "For incoming categories, actual higher than forecast is favourable. For outgoing categories, actual lower than forecast is favourable.",
						},
					],
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					title: "How do I drill into a category?",
					items: [
						{ text: "Click on any category row in the table." },
						{
							text: "The Detail Panel automatically opens below the selected row. You can close it by clicking the row again.",
						},
						{
							text: "Review the individual transactions that made up the actual figure for that category.",
						},
						{
							text: "Check the Source Chip to confirm which system the transactions came from.",
							note: "This is your audit trail — from the variance figure all the way back to the originating transaction in the source system.",
						},
					],
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					title: "How do I change the analysis period?",
					items: [
						{ text: "Click the **Period Selector** at the top of the screen." },
						{
							text: "Choose from current week, current month, last month, or current quarter.",
						},
						{
							text: "The table and summary bar update immediately to reflect the selected period.",
						},
					],
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					title: "How do I take a forecast snapshot?",
					items: [
						{
							text: "Click the **camera icon** in the top-right of the Variance Analysis page.",
						},
						{
							text: 'Enter a name for the snapshot — for example "Q1 Close" or "Pre-budget baseline".',
						},
						{
							text: "Add an optional description to explain the context of this snapshot.",
						},
						{
							text: "Select the **Snapshot Period** — how far forward the forecast should project. Options are 90 days, 6 months, 12 months, or 3 years.",
							note: "The longer the horizon, the more future periods you will be able to compare against this snapshot later.",
						},
						{
							text: "Click **Take Snapshot**. The snapshot is saved and immediately available in the Snapshot Selector.",
						},
					],
					requiredPermission: "manage_forecast_snapshots",
				},
				{
					title: "How do I compare a historical snapshot against actuals?",
					items: [
						{
							text: "Click the **Snapshot Selector** dropdown at the top of the page.",
						},
						{
							text: "Select the snapshot you want to compare against — for example the one taken at the start of the period you are analysing.",
						},
						{
							text: "A blue context banner confirms which snapshot is active.",
						},
						{
							text: "Review the Category Table — the Forecast column now shows what the engine predicted at the time of the snapshot, not the current live forecast.",
							note: "If the snapshot horizon is shorter than your selected analysis period, an amber warning will appear. This means comparison figures only cover part of the period.",
						},
						{
							text: "To return to the live forecast, select **Live (current)** from the Snapshot Selector.",
						},
					],
					requiredPermission: "view_variance_analysis_screen",
				},
				{
					title: "How do I archive a snapshot I no longer need?",
					items: [
						{
							text: "Select the snapshot in the **Snapshot Selector** dropdown.",
						},
						{
							text: "Click the **archive icon** (bin icon) next to the snapshot pill.",
						},
						{
							text: "Review the confirmation dialog — it shows the snapshot name, date, and a clear warning that this baseline cannot be recreated once archived.",
						},
						{
							text: "Click **Archive Snapshot** to confirm.",
							note: "Archiving is permanent. If there is any chance you may need this snapshot for future comparison, do not archive it. If it was taken during a data error, note this in the description before archiving.",
						},
					],
					requiredPermission: "delete_forecast_snapshots",
				},
			],
			proTips: [
				"Consistent unfavourable variances in the same category are a signal to review the forecast assumption for that category — the model may be systematically over-optimistic.",
				"Use the drill-down to check whether a large variance is caused by one transaction or many — a single large item is easier to explain than a systemic pattern.",
				"Month-end is the most valuable time to run this analysis — actual figures are complete and the next month's forecast can be recalibrated.",
				"Share variance commentary with budget holders for the categories they own — it keeps them accountable and improves future forecasting.",
				"The system takes automatic snapshots at month-end, year-end, and on budget activation — so you will always have historical baselines available even if you never take a manual one.",
				"When taking a manual snapshot before a significant business event (board meeting, budget submission, acquisition), give it a descriptive name so it is easy to find months later.",
			],
		},
		faq: [
			{
				question: "What counts as a favourable variance?",
				answer:
					"For incoming categories (receipts, income), a favourable variance means actual cash received was higher than forecast. For outgoing categories (payments, costs), a favourable variance means actual cash paid was lower than forecast. In both cases, the business's cash position ended up better than expected.",
				requiredPermission: "view_variance_analysis_screen",
			},
			{
				question: "Can I export the variance analysis?",
				answer:
					"Use the Reports section to generate a formal variance report for a specific period. The Reports page provides formatted outputs suitable for sharing.",
				requiredPermission: "view_reports",
			},
			{
				question:
					"Why does the variance analysis only go back a limited period?",
				answer:
					"The analysis covers periods for which both forecast and actual data are available. The further back you look, the less relevant the data is to current forecasting. Focus on the most recent complete period for the most actionable insights.",
				requiredPermission: "view_variance_analysis_screen",
			},
			{
				question: "Can I add commentary to explain a variance?",
				answer:
					"Variance commentary can be captured via the Actions page — create an action to record the investigation and outcome. This creates an audit trail of variance explanations over time.",
			},
			{
				question:
					"What is the difference between a manual and an automatic snapshot?",
				answer:
					"Automatic snapshots are taken by the system at month-end, year-end, and when a budget scenario is activated — they are always-on guardrails that ensure variance analysis always has historical baselines to work with. Manual snapshots are ones you take yourself at any point, for any reason. Both appear in the same Snapshot Selector and work identically.",
				requiredPermission: "view_variance_analysis_screen",
			},
			{
				question: "Can I recover a snapshot after archiving it?",
				answer:
					"No. Archiving a snapshot permanently removes it from the system. The forecast engine cannot recreate what it predicted on a past date. If you are unsure whether you need a snapshot, keep it.",
				requiredPermission: "delete_forecast_snapshots",
			},
			{
				question: "What happens if I have no snapshots at all?",
				answer:
					"If automatic month-end snapshots are disabled in System Settings and no manual snapshots have been taken, the Variance Analysis page will show an empty state. Variance analysis requires at least one snapshot to provide a historical forecast baseline. You can take a snapshot at any time using the camera icon, or re-enable automatic snapshots in System Settings → Snapshots.",
				requiredPermission: "view_variance_analysis_screen",
			},
		],
		relatedPages: [
			{ label: "Transactions", pageKey: "transactions" },
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
			{ label: "Actions", pageKey: "actions" },
			{ label: "Reports", pageKey: "reports" },
		],
	},

	// ── AI RECOMMENDATIONS ────────────────────────────────────────
	recommendations: {
		pageTitle: "AI Recommendations",
		description:
			"Automatically surfaced insights and suggested actions based on patterns in your cash flow data.",
		icon: "HelpCircle",
		iconColor: "bg-purple-500",
		functionality: {
			overview:
				"The Recommendations page surfaces insights that have been automatically identified from your cash flow data. These are not instructions — they are prompts for discussion and investigation. Each recommendation explains why it was surfaced, what it means for the business, and what actions could be considered. Your data never leaves your organisation.",
			keyFeatures: [
				{
					label: "Operational Insights",
					detail:
						"Short-term observations about cash flow patterns — late payments, upcoming shortfalls, unusual transaction activity.",
					requiredPermission: "view_operational_insights",
				},
				{
					label: "Strategic Insights",
					detail:
						"Longer-term observations about financial health, working capital efficiency, and planning opportunities.",
					requiredPermission: "view_strategic_insights",
				},
				{
					label: "Why It Was Surfaced",
					detail:
						"Every recommendation includes a plain-language explanation of the data pattern that triggered it — so you always know the basis for the suggestion.",
				},
				{
					label: "Suggested Actions",
					detail:
						"Each recommendation includes one or more suggested next steps, which can be turned into tracked actions.",
				},
				{
					label: "Create Action",
					detail:
						"Convert a recommendation directly into a tracked action, assigned to a team member, with a due date.",
					requiredPermission: "create_action_from_recommendation",
				},
				{
					label: "Generate Recurring Transaction",
					detail:
						"Where an insight identifies a missing or incomplete recurring transaction, generate a pre-populated Recurring Transaction entry directly from the recommendation card — fully editable before saving.",
					requiredPermission: "manage_recurring_transactions",
				},
				{
					label: "Dismiss",
					detail:
						"Mark a recommendation as reviewed and dismiss it if no action is needed.",
					requiredPermission: "dismiss_recommendation",
				},
				{
					label: "Impact History",
					detail:
						"Each recommendation's projected improvement value carries a history icon. Click it to see how the projected impact has changed across forecast refresh runs — showing the previous and new value for each recalculation.",
				},
			],
			sectionsAndFields: [
				{
					name: "About These Recommendations",
					description:
						"A collapsible disclosure panel explaining how recommendations are generated and confirming that your data never leaves your organisation.",
				},
				{
					name: "Operational Insights Tab",
					description:
						"Short-term cash flow observations relevant to day-to-day management.",
				},
				{
					name: "Strategic Insights Tab",
					description:
						"Longer-term observations relevant to planning and decision-making.",
				},
				{
					name: "Recommendation Card",
					description:
						"Each card shows a title, summary, why it was surfaced, suggested actions, priority level, and creation date.",
				},
				{
					name: "Priority Badge",
					description:
						"High, Medium, or Low — indicating the relative urgency of the recommendation.",
				},
				{
					name: "Create Action Button",
					description:
						"Opens the action creation form pre-populated with details from the recommendation.",
				},
				{
					name: "Generate Recurring Transaction Button",
					description:
						"Appears on cards where the insight identifies a missing or incomplete recurring transaction. Opens the Recurring Transaction modal pre-populated with the AI suggestion — fully editable before saving.",
				},
				{
					name: "Dismiss Button",
					description:
						"Removes the recommendation from the active list once it has been reviewed.",
				},
				{
					name: "AI-suggested Badge",
					description:
						"Appears on Recurring Transactions generated from this screen. Transitions to Manually confirmed once the user edits and saves the entry.",
				},
			],
			useCases: [
				"Reviewing weekly insights to stay ahead of emerging cash flow issues before they become urgent.",
				"Using a strategic insight as the basis for a finance team discussion about working capital.",
				"Converting a recommendation into a tracked action so it does not get forgotten.",
				"Using an AI-suggested recurring transaction to fill a gap in the Base forecast — for example a missing VAT payment or payroll entry.",
				"Dismissing recommendations that are not relevant to the current business context.",
				"Sharing an insight with a colleague by creating an action assigned to them.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Recommendations** in the sidebar. Read the **About these recommendations** disclosure at the top of the screen — it explains how the system works and how your data is handled. Then review insights on the **Operational Insights** or **Strategic Insights** tabs depending on your focus.",
			steps: [
				{
					title: "How do I understand why a recommendation was surfaced?",
					items: [
						{ text: "Click **Recommendations** in the sidebar." },
						{ text: "Find the recommendation card you want to review." },
						{ text: "Read the **Why this was surfaced** section on the card." },
						{
							text: "Use this context to decide whether the recommendation is relevant to your current situation.",
							tip: 'The "why" section is the most important part — a recommendation without context is just a suggestion. With context, it becomes a data-backed prompt for action.',
						},
					],
				},
				{
					title: "How do I create an action from a recommendation?",
					items: [
						{ text: "Find the recommendation you want to act on." },
						{ text: "Click **Create Action** on the card." },
						{
							text: "The action creation form automatically opens pre-populated with the recommendation title and suggested steps.",
						},
						{ text: "Assign it to a team member and set a due date." },
						{
							text: "Click **Save**.",
							note: "A toast message confirms the action has been created. The action appears on the Actions screen and in the assignee's action list.",
						},
					],
					requiredPermission: "create_action_from_recommendation",
				},
				{
					title:
						"How do I generate a recurring transaction from a recommendation?",
					items: [
						{
							text: "Find the recommendation card that identifies a missing or incomplete recurring transaction.",
						},
						{
							text: "Click **Generate Recurring Transaction** on the card.",
							note: "This button only appears on recommendations where the AI has identified a specific recurring transaction pattern. It is not available on all cards.",
						},
						{
							text: "The Recurring Transaction modal opens pre-populated with the AI suggestion — amount, frequency, direction, and description.",
						},
						{
							text: "Review and adjust all fields as needed. The suggestion is a starting point, not a confirmed value.",
						},
						{
							text: "Click **Save**.",
							note: "A toast message confirms the recurring transaction has been created. It appears in the Recurring Transactions screen with an AI-suggested badge. The recommendation is automatically marked as reviewed.",
						},
						{
							text: "When you next edit and save that recurring transaction, the badge transitions from AI-suggested to Manually confirmed.",
							tip: "Always review the suggested amount before saving — AI-suggested values are estimates based on historical patterns, not confirmed transaction values.",
						},
					],
					requiredPermission: "manage_recurring_transactions",
				},
				{
					title: "How do I dismiss a recommendation?",
					items: [
						{ text: "Find the recommendation you want to dismiss." },
						{ text: "Click **Dismiss** on the recommendation card." },
						{
							text: "The recommendation is removed from the active list.",
							note: "Dismissed recommendations are not deleted — they are recorded in the system for audit purposes and removed from the active list.",
						},
					],
					requiredPermission: "dismiss_recommendation",
				},
				{
					title: "How do I view the impact history for a recommendation?",
					items: [
						{ text: "Find the recommendation card you want to review." },
						{ text: "Locate the projected improvement value on the card." },
						{ text: "Click the **history icon** (clock) next to the value." },
						{
							text: "The Impact History panel opens, showing a table of every change to the projected impact value across all previous forecast refresh runs.",
							note: 'Each row shows the date, refresh type (Scheduled or Manual), who triggered the refresh, the previous value, and the new value. If the value has not changed since first calculated, the panel shows "No changes since first calculated."',
						},
						{
							text: "All values are shown in full precision regardless of your display preference setting.",
							tip: "Stable impact values suggest a well-understood recommendation. Fluctuating values may indicate the underlying data is still settling — treat the recommendation with appropriate caution.",
						},
					],
				},
			],
			proTips: [
				'Read the "About these recommendations" disclosure once — it answers the most common question about how the system works.',
				"Treat recommendations as prompts for conversation, not instructions. The best outcome is a team discussion that leads to a considered decision.",
				"High priority recommendations deserve attention within 24 hours. Medium and low priority items can be reviewed in a weekly cadence.",
				"If a recommendation keeps reappearing, it may indicate a systemic pattern that needs a more fundamental fix rather than a one-off action.",
				"AI-suggested recurring transactions are estimates — always review and confirm the value before your next forecast cycle. The Manually confirmed badge tells you and your team that a human has reviewed the entry.",
			],
		},
		faq: [
			{
				question:
					"Is my data being shared externally to generate these recommendations?",
				answer:
					"No. Your data never leaves your organisation. Recommendations are generated from three sources: patterns within your own data, financial best practice rules built into the system, and benchmarks configured by your system administrator. No data is shared with or compared against other organisations.",
			},
			{
				question: "How often are new recommendations generated?",
				answer:
					"In Live Mode, recommendations are refreshed each time the system syncs with your connected data sources. In Demo Mode, recommendations are static examples to illustrate the capability.",
			},
			{
				question: "Can I see recommendations that have been dismissed?",
				answer:
					"Dismissed recommendations are recorded in the system activity log. Contact your system administrator if you need to review previously dismissed items.",
				requiredPermission: "dismiss_recommendation",
			},
			{
				question:
					"What is the difference between Operational and Strategic insights?",
				answer:
					"Operational insights are short-term observations — typically relevant within the next 30-90 days — about cash flow patterns, upcoming risks, and immediate opportunities. Strategic insights are longer-term observations about financial health, trends, and planning considerations, typically relevant over a 6-24 month horizon.",
			},
			{
				question: "Can I turn off recommendations I do not find useful?",
				answer:
					"Individual recommendations can be dismissed. If a category of recommendation is consistently not relevant, speak to your system administrator about adjusting the recommendation rules for your organisation.",
			},
			{
				question: "What does the Generate Recurring Transaction button do?",
				answer:
					"It opens the Recurring Transaction creation modal pre-populated with values suggested by the AI — amount, frequency, direction, and description. All fields are fully editable before saving. The entry is saved with an AI-suggested badge so you and your team always know its origin. It only appears on recommendation cards where the AI has identified a specific missing or incomplete recurring transaction pattern.",
				requiredPermission: "manage_recurring_transactions",
			},
			{
				question:
					"What is the difference between AI-suggested and Manually confirmed on a recurring transaction?",
				answer:
					"AI-suggested means the entry was generated from an AI recommendation and has not yet been reviewed or edited by a user. Manually confirmed means a user has since edited and saved the entry — confirming they have reviewed the values. This distinction is visible in the Recurring Transactions screen so your team always knows which entries have been human-reviewed.",
				requiredPermission: "manage_recurring_transactions",
			},
		],
		relatedPages: [
			{ label: "Actions", pageKey: "actions" },
			{ label: "Dashboard", pageKey: "dashboard" },
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
		],
	},

	// ── SCENARIOS ─────────────────────────────────────────────────
	scenarios: {
		pageTitle: "Scenarios",
		description:
			"Model what-if situations to understand how potential changes would affect the cash forecast.",
		icon: "TrendingUp",
		iconColor: "bg-teal-500",
		functionality: {
			overview:
				"Scenarios allow you to model hypothetical changes to the forecast — such as winning a new contract, losing a key customer, or making a capital investment — and see exactly how each change would affect the projected cash position. Scenarios are always overlays on the base forecast and never alter the underlying data.",
			keyFeatures: [
				{
					label: "Scenario Gallery",
					detail:
						"View all saved scenarios at a glance — with status, creator, and a summary of the cash impact.",
					requiredPermission: "view_scenarios",
				},
				{
					label: "Create Scenario",
					detail:
						"Build a new scenario by adding revenue and cost adjustments, one-off items, or timing changes.",
					requiredPermission: "create_scenarios",
				},
				{
					label: "Scenario Builder",
					detail:
						"A structured editor for adding, editing, and removing adjustments within a scenario.",
					requiredPermission: "create_scenarios",
				},
				{
					label: "Impact Analysis",
					detail:
						"See a summary of the net cash impact of the scenario — month by month and in total.",
					requiredPermission: "view_scenarios",
				},
				{
					label: "Compare Scenarios",
					detail:
						"Place two or more scenarios side by side to compare their cash impact and choose between options.",
					requiredPermission: "compare_scenarios",
				},
				{
					label: "Impact History",
					detail:
						"Each scenario's impact value carries a history icon. Click it to see a drill-down of every value change across all previous refresh runs — showing what the impact was before and after each recalculation.",
					requiredPermission: "view_scenarios",
				},
				{
					label: "Submit for Review",
					detail:
						"Send a Draft scenario for informal peer review before entering the formal approval workflow. The reviewer confirms they have looked at it, then the creator escalates to the approval workflow when ready.",
				},
				{
					label: "Mark as Reviewed",
					detail:
						"For users the scenario has been shared with: confirm you have reviewed it. This stamps the scenario as reviewed and signals to the creator it is ready to move forward — it does not change the scenario status.",
				},
				{
					label: "Send for Approval",
					detail:
						"As the scenario creator, formally submit the scenario into the approval workflow — either after peer review or directly from Draft by skipping review. The scenario enters Stage 1 of the approval workflow (Pending Approval).",
				},
				{
					label: "Approval Stages",
					detail:
						"Each approval workflow has one or more approval stages. Each stage must be approved by the designated approver before the scenario advances to the next stage. Final stage approval makes the scenario Active.",
				},
				{
					label: "Withdraw Submission",
					detail:
						"As the scenario creator, recall a scenario from the approval workflow or peer review at any point before final approval. The scenario returns to Draft so you can make changes and resubmit.",
				},
			],
			sectionsAndFields: [
				{
					name: "Scenario Card",
					description:
						"Each card in the gallery shows the scenario name, status, creator, last updated date, and a net cash impact summary.",
				},
				{
					name: "Status Badge",
					description:
						"Draft, Under Review, Pending Approval, Active, Committed, Parked, or Archived — the current stage of the scenario in the workflow. Under Review means the scenario is in informal peer review — it has not yet been submitted into the formal approval workflow. Pending Approval means it is in the formal approval workflow, moving through one or more approval stages.",
				},
				{
					name: "Builder Tab",
					description:
						"The editor where revenue and cost adjustments are added and managed.",
				},
				{
					name: "Impact Tab",
					description:
						"A visual and tabular summary of the scenario's net effect on the forecast.",
				},
				{
					name: "Discussion Tab",
					description:
						"A comment thread for collaborating on the scenario with other team members.",
				},
				{
					name: "History Tab",
					description:
						"A log of all changes made to the scenario, including who made them and when.",
				},
				{
					name: "Dependencies Tab",
					description:
						"Links to other scenarios or decisions that this scenario depends on.",
				},
				{
					name: "Sharing Tab",
					description:
						"Controls for sharing the scenario with specific users or groups.",
				},
				{
					name: "Compare View",
					description:
						"A side-by-side view of two or more scenarios showing their relative cash impact.",
				},
			],
			useCases: [
				"Modelling the cash impact of winning a large new contract before committing to additional resource.",
				"Understanding the downside position if a key customer delays payment by 60 days.",
				"Comparing three different capex options to understand which has the least impact on working capital.",
				"Building a conservative scenario to present to a lender alongside the base forecast.",
				"Running a headcount reduction scenario to quantify the cash benefit of a restructure.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Scenarios** in the sidebar. The gallery shows all scenarios you have access to. Click any card to open the detail view, or click **New Scenario** to start building.",
			steps: [
				{
					title: "How do I create a new scenario?",
					items: [
						{ text: "Click **New Scenario** at the top of the gallery." },
						{ text: "Enter a name for the scenario." },
						{
							text: "Choose a starting point. **Start from Base Forecast** is currently available — this builds the scenario on top of the live base forecast so all adjustments are relative to it.",
							note: "Start Blank (building a scenario with a zeroed base) is coming in a future release.",
						},
						{
							text: "Click **Create**.",
							tip: 'Give the scenario a clear, descriptive name — for example "Q3 New Contract Win" rather than "Scenario 3" — so it is easy to identify when comparing.',
						},
						{
							text: "The scenario is saved as a Draft. You are taken straight into the Builder tab.",
						},
					],
					requiredPermission: "create_scenarios",
				},
				{
					title: "How do I add adjustments in the Scenario Builder?",
					items: [
						{ text: "Open the scenario and click the **Builder** tab." },
						{ text: "Click **Add Revenue Item** or **Add Cost Item**." },
						{
							text: "Enter the amount, timing, and description for each adjustment.",
						},
						{
							text: "Click **Save** on each item.",
							note: "Adjustments are always relative to the base forecast. You are modelling the difference, not replacing the base figures. The Impact tab updates automatically.",
						},
					],
					requiredPermission: "create_scenarios",
				},
				{
					title: "How do I compare two scenarios?",
					items: [
						{
							text: "In the scenario gallery, click **Compare** on a scenario card.",
						},
						{
							text: "The comparison view opens with that scenario pre-selected.",
						},
						{
							text: "Add one or more additional scenarios using the selector.",
						},
						{
							text: "Review the side-by-side cash impact view.",
							tip: "Always include the base forecast as one of the comparison items so you can see the net difference against the current position.",
						},
					],
					requiredPermission: "compare_scenarios",
				},
				{
					title: "How do I submit a scenario for informal peer review?",
					items: [
						{ text: "Open the scenario and confirm it is complete." },
						{ text: "Click **Submit for Review**." },
						{
							text: "In the dialog, select **Send for Review**.",
							tip: "Check your shared-with list before submitting — reviewers need to be on it to see the Mark as Reviewed button.",
						},
						{ text: "Add an optional covering note and click **Confirm**." },
						{
							text: "The scenario moves to Under Review status. This is informal only — the scenario has NOT entered the formal approval workflow.",
						},
						{
							text: "Once reviewers have confirmed, click **Send for Approval** to formally submit into Stage 1 of the approval workflow.",
							note: "Under Review is a holding state the creator controls. Only the creator can escalate it into the formal approval workflow.",
						},
					],
				},
				{
					title:
						"How do I send a scenario directly for approval (skip review)?",
					items: [
						{ text: "Open the Draft scenario." },
						{ text: "Click **Submit for Review**." },
						{ text: "In the dialog, select **Skip to Approval**." },
						{ text: "Add an optional covering note and click **Confirm**." },
						{
							text: "The scenario moves straight to Pending Approval at Stage 1 of the approval workflow.",
							tip: "Use this path when a peer review step is not needed — for example, a scenario you have already discussed with approvers informally.",
						},
					],
				},
				{
					title: "How does multi-stage approval work?",
					items: [
						{
							text: "When a scenario enters Pending Approval it starts at Stage 1 of the configured workflow.",
						},
						{
							text: "The Stage 1 approver clicks **Approve & Forward** to advance the scenario to Stage 2.",
						},
						{
							text: "Each subsequent approver advances the scenario through the workflow stages in order.",
						},
						{
							text: "The final stage approver clicks **Final Approve** — the scenario becomes Active.",
							note: "The number of approval stages is configured in the Approval Workflows admin page. A 2-stage workflow gives two approval steps — not one review step and one approval step.",
						},
						{
							text: "Any stage approver can also click **Reject** — this returns the scenario to Draft for the creator to revise and resubmit.",
						},
					],
				},
				{
					title: "How do I mark a scenario as reviewed?",
					items: [
						{
							text: "Confirm the scenario creator has shared it with you and submitted it for informal review.",
						},
						{
							text: "Find the scenario in the gallery — it shows Under Review status.",
						},
						{
							text: "Open the scenario and click **Mark as Reviewed**.",
							note: "Marking as reviewed does not submit the scenario into the approval workflow. Only the creator can do that by clicking Send for Approval.",
						},
					],
				},
				{
					title: "How do I withdraw a submission?",
					items: [
						{
							text: "Find the scenario in the gallery — it shows Under Review or Pending Approval status.",
						},
						{ text: "Open the scenario and click **Withdraw**." },
						{ text: "Click **Confirm** in the dialog." },
						{
							text: "The scenario returns to Draft status and any approval progress is reset.",
							note: "Only the scenario creator can withdraw. If a scenario has been rejected by an approver, revise it from Draft and resubmit.",
						},
					],
				},
				{
					title: "How do I view the impact history for a scenario?",
					items: [
						{
							text: "Locate the scenario card in the gallery, or open the scenario detail view.",
						},
						{
							text: "Find the net cash impact figure — it is shown in the impact summary section.",
						},
						{
							text: "Click the **history icon** (clock) next to the impact value.",
						},
						{
							text: "The Impact History panel opens, showing a table of every change to the impact value across all previous forecast refresh runs.",
							note: 'Each row shows the date, refresh type (Scheduled or Manual), who triggered the refresh, the previous value, and the new value. If the impact value has not changed since it was first calculated, the panel shows "No changes since first calculated."',
						},
						{
							text: "All values in the table are shown in full precision regardless of your display preference setting.",
							tip: "Use the impact history to spot whether scenario adjustments are stable or fluctuating — large swings between refreshes can indicate a data source inconsistency.",
						},
					],
					requiredPermission: "view_scenarios",
				},
			],
			proTips: [
				"Always build at least two scenarios — a base case and a conservative case. Decisions made on a single scenario are fragile.",
				"Use the Discussion tab to record the reasoning behind each adjustment — this is valuable context when reviewing the scenario months later.",
				"Park scenarios you are not actively using rather than deleting them — they may be useful as a reference later.",
				"The Compare view is most powerful when comparing scenarios with materially different assumptions — small differences are hard to read in a chart.",
			],
		},
		faq: [
			{
				question: "Does a scenario change the base forecast?",
				answer:
					"No. Scenarios are always overlays. They show you what the forecast would look like if the scenario were true, but they never change the underlying base forecast data. The base forecast only changes when actual transaction data or recurring templates change.",
				requiredPermission: "view_scenarios",
			},
			{
				question: 'What does "Committed" status mean?',
				answer:
					"Committed is a governance status for scenarios that have been formally signed off as the agreed planning position. Like Active scenarios, Committed scenarios are overlays on the base forecast — they never alter the underlying data. The distinction signals to the team that this version of the future is the one the business is committed to planning against.",
			},
			{
				question:
					"What is the difference between Under Review and Pending Approval?",
				answer:
					"Under Review is an informal peer review state — the creator has shared the scenario with colleagues to sense-check it, but it has NOT entered the formal approval workflow. Pending Approval means the scenario has been formally submitted into the approval workflow and is moving through one or more approval stages. Under Review is optional and creator-controlled. Pending Approval is the formal gate before Active.",
			},
			{
				question: "What does the reviewer actually do?",
				answer:
					'A reviewer — someone the scenario has been shared with — clicks "Mark as Reviewed" to confirm they have looked at the scenario. This stamps it as reviewed but does NOT submit it into the approval workflow. Only the creator can escalate it by clicking "Send for Approval". Reviewers cannot approve or reject a scenario — that is the role of the formal approvers configured in the workflow.',
			},
			{
				question: "How many approval stages can a workflow have?",
				answer:
					"As many as you need — this is configured in the Approval Workflows admin page. A 2-stage workflow means two separate approvers must each sign off before the scenario goes Active. Review (Under Review status) is separate from and additional to the approval stages — it is an informal pre-submission step, not one of the numbered workflow stages.",
			},
			{
				question: "Can I edit a scenario after it has been submitted?",
				answer:
					"Not directly. Once in the approval workflow (Pending Approval), a scenario is locked for editing. If you need to make changes, use the Withdraw button to pull it back to Draft — you can then edit and resubmit. If it is still Under Review (informal peer review), you can also Withdraw to make changes before formally submitting for approval.",
			},
			{
				question: "How many scenarios can I have at once?",
				answer:
					"There is no fixed limit. However, having too many active scenarios makes the gallery hard to manage. Use the Park status to archive scenarios you are not actively using without deleting them.",
				requiredPermission: "view_scenarios",
			},
			{
				question: "Who can see my scenarios?",
				answer:
					'All users with the "View scenarios" permission can see all scenarios in the gallery. Use the Sharing tab to control visibility if needed.',
				requiredPermission: "view_scenarios",
			},
		],
		relatedPages: [
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
			{ label: "12-Month Forecast", pageKey: "forecast-12m" },
			{ label: "3-Year Forecast", pageKey: "forecast-3y" },
			{ label: "Actions", pageKey: "actions" },
		],
	},

	// ── ACTIONS ───────────────────────────────────────────────────
	actions: {
		pageTitle: "Actions",
		description:
			"Create, assign, and track tasks arising from cash flow insights and recommendations.",
		icon: "HelpCircle",
		iconColor: "bg-rose-500",
		functionality: {
			overview:
				"The Actions screen is where cash flow observations become accountable tasks. When a variance needs investigating, a recommendation needs following up, or a decision needs documenting, an action provides the structure to assign it to someone, set a deadline, and track it to completion. Actions create an auditable record of how the business responds to its cash flow position. Completed and archived actions are retained for 12 months, giving you a full history of how the team has responded to cash flow issues.",
			keyFeatures: [
				{
					label: "Action List",
					detail:
						"View all open, completed, and archived actions — yours and your team's — filtered by status, priority, or assignee. Use the My Actions / All Actions / Assigned by Me tabs to switch views.",
				},
				{
					label: "Create Action",
					detail:
						"Create a new action with a title, description, assignee (including yourself), due date, and priority level.",
					requiredPermission: "create_actions",
				},
				{
					label: "Assign to Team Members",
					detail:
						"Assign any action to yourself or another user so accountability is clear and visible to everyone. When you reassign an action, the original record is archived and a new action is created for the new assignee — keeping each person's history clean and separate.",
				},
				{
					label: "Due Date Indicators",
					detail:
						"Actions display a due date pill showing their urgency: Overdue (red), Due Today (amber), or Due Soon (amber, within 5 days). No pill is shown for actions with more than 5 days remaining.",
				},
				{
					label: "Mark as Complete",
					detail:
						"Click the complete button on the right side of an action card to mark it done. The action moves to the Completed tab and is retained for 12 months.",
					requiredPermission: "complete_actions",
				},
				{
					label: "Archive",
					detail:
						"Click the archive icon (bin) to archive an action that is no longer relevant. Archived actions are retained for 12 months and are visible in the Archived tab — they are never permanently deleted immediately.",
				},
				{
					label: "Edit Actions",
					detail:
						"Click an action card in the Assigned by Me tab to open it in edit mode. This includes actions you assigned to yourself — they appear in both the My Actions and Assigned by Me tabs. Change the title, description, priority, due date, or assignee. Changing the assignee triggers the reassignment pattern — original archived, new action created.",
					requiredPermission: "create_actions",
				},
				{
					label: "Comments",
					detail:
						"Add comments to an action to record progress updates, decisions made, or additional context. Comments form a discussion thread visible to all users with access to the action.",
				},
				{
					label: "12-Month Retention",
					detail:
						"Completed and archived actions are retained for 12 months before being automatically removed. This gives the team a full year of history to refer back to without requiring manual record-keeping.",
				},
			],
			sectionsAndFields: [
				{
					name: "My Actions tab",
					description:
						"All actions currently assigned to you — open, in progress, and due soon.",
				},
				{
					name: "All Actions tab",
					description:
						"All actions across the team. Use the status and priority filters to focus on specific items.",
				},
				{
					name: "Assigned by Me tab",
					description:
						"Actions you created — whether assigned to others or to yourself. Click a card to open it in edit mode.",
				},
				{
					name: "Completed tab",
					description:
						"Actions marked as complete, retained for 12 months. Read-only — use Reinstate if an action needs to be reopened.",
				},
				{
					name: "Archived tab",
					description:
						"Actions that have been archived. Retained for 12 months then automatically removed. A retention notice is shown at the top of this tab.",
				},
				{
					name: "Status Filter",
					description:
						"Filter between Open, In Progress, Completed, and All actions.",
				},
				{
					name: "Priority Filter",
					description:
						"Filter by High, Medium, or Low priority to focus on the most urgent items.",
				},
				{
					name: "Action Card",
					description:
						"Each card shows the title, assignee, due date pill, priority badge, status, and a comment count. Icon buttons on the right: complete (tick), archive (bin), reinstate (undo).",
				},
				{
					name: "Due Date Pill",
					description:
						"Overdue (red), Due Today (amber), Due Soon within 5 days (amber), or no pill if more than 5 days remain.",
				},
				{
					name: "Priority Badge",
					description:
						"High, Medium, or Low — set when creating the action to indicate relative urgency.",
				},
				{
					name: "Comments Thread",
					description:
						"A running log of notes and updates added by any user with access to the action.",
				},
			],
			useCases: [
				"Creating an action to investigate why a supplier payment category is consistently over-forecast.",
				"Assigning a follow-up task to a colleague to chase an overdue invoice.",
				"Assigning an action to yourself as a personal cash flow task — you appear in your own assignee list.",
				"Recording the outcome of a variance investigation so there is an audit trail.",
				"Converting a recommendation into a tracked action with a deadline and owner.",
				"Editing an action in Assigned by Me to update the due date or reassign it to someone else.",
				"Reviewing all open high-priority actions in a weekly team meeting.",
				"Checking the Archived tab to review how the team responded to a cash flow issue earlier in the year.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Actions** in the sidebar. The screen defaults to My Actions — all actions currently assigned to you. Use the **All Actions** and **Assigned by Me** tabs to switch views, and the status and priority filters to focus on specific items.",
			steps: [
				{
					title: "How do I create a new action?",
					items: [
						{ text: "Click **New Action** at the top of the screen." },
						{
							text: "Enter a title and description — be specific about what needs to be done and why.",
						},
						{ text: "Set a due date and priority level." },
						{
							text: "Select an assignee — you can assign the action to yourself or to another team member.",
						},
						{
							text: "Click **Save**.",
							tip: 'A clear, specific title makes actions easier to manage at scale. "Investigate Q3 payroll variance" is better than "Check payroll".',
						},
					],
					requiredPermission: "create_actions",
				},
				{
					title: "How do I mark an action as complete?",
					items: [
						{ text: "Find the action in the list." },
						{
							text: "Click the **complete** button (tick icon) on the right side of the action card.",
						},
						{
							text: "The action moves to the Completed tab and is recorded with the completion date.",
							tip: "Add a final comment before completing to record what was done and what the outcome was — this is the audit trail.",
						},
					],
					requiredPermission: "complete_actions",
				},
				{
					title: "How do I archive an action?",
					items: [
						{ text: "Find the action in the list." },
						{
							text: "Click the **Archive** button (bin icon) on the right side of the action card.",
						},
						{
							text: "The action moves to the Archived tab.",
							note: "Archived actions are retained for 12 months and can be reinstated if needed. They are never immediately deleted.",
						},
					],
				},
				{
					title: "How do I edit an action?",
					items: [
						{
							text: "Click the **Assigned by Me** tab — this shows all actions you created, including any you assigned to yourself.",
						},
						{ text: "Click the action card you want to edit." },
						{
							text: "The action opens in edit mode, pre-populated with all current values.",
						},
						{ text: "Make your changes and click **Save Changes**." },
						{
							text: "If you change the assignee, an amber warning appears below the assignee field.",
							note: "Changing the assignee archives the original action and creates a new one for the new assignee. This keeps each person's action history clean and separate.",
						},
					],
					requiredPermission: "create_actions",
				},
				{
					title: "How do I add a comment to an action?",
					items: [
						{ text: "Click on an action card to open it." },
						{ text: "Scroll to the **Comments** section." },
						{
							text: "Type your comment and click **Post**.",
							note: "Comments are visible to all users who can see the action. Use them to record decisions, progress updates, and relevant context.",
						},
					],
				},
				{
					title: "How do I reinstate a completed or archived action?",
					items: [
						{ text: "Click the **Completed** or **Archived** tab." },
						{ text: "Find the action you want to reopen." },
						{
							text: "Click the **Reinstate Action** button (undo icon) on the right side of the card.",
						},
						{
							text: "The action returns to Open status and appears in the main action list.",
						},
					],
				},
			],
			proTips: [
				"Review open high-priority actions every morning — they represent the most urgent cash flow issues requiring attention.",
				"Add a comment when you make progress on an action, not just when it is complete — this keeps the whole team informed.",
				"Use the Archive button rather than ignoring actions that are no longer relevant. A clean action list makes it easier to spot what actually matters.",
				"Link actions to recommendations by creating them directly from the Recommendations screen — they arrive pre-populated with the relevant context.",
				"The Completed and Archived tabs retain 12 months of history — useful for retrospectives and audit queries without any extra record-keeping.",
			],
		},
		faq: [
			{
				question: "Can I see actions assigned to other people?",
				answer:
					"Yes — use the All Actions tab to see actions across the team. The My Actions tab filters to only actions assigned to you. The Assigned by Me tab shows actions you have created and assigned to others.",
			},
			{
				question: "Can I assign an action to myself?",
				answer:
					'Yes — your own name appears in the assignee list with a "(You)" label. Actions is not just a delegation tool — it works equally well as a personal cash flow task list. Self-assigned actions appear in both the My Actions tab and the Assigned by Me tab — go to Assigned by Me to edit them.',
			},
			{
				question: "What do the due date colours mean?",
				answer:
					'Red means the action is overdue. Amber with "Due Today" means the deadline is today. Amber with "Due Soon" means the deadline is within the next 5 days. No pill is shown if there are more than 5 days remaining.',
			},
			{
				question:
					"What is the difference between archiving and completing an action?",
				answer:
					"Completing an action means the work is done — it moves to the Completed tab with a completion date recorded. Archiving means the action is no longer relevant — it moves to the Archived tab without a completion record. Both are retained for 12 months.",
			},
			{
				question: "What happens when I reassign an action?",
				answer:
					"When you change the assignee on an existing action and save, the original action is archived (sealing that person's history) and a new action is created for the new assignee. This keeps each person's action history clean and ensures accountability is clearly recorded.",
			},
			{
				question: "Are actions permanently deleted?",
				answer:
					"No. The bin icon archives an action — it does not delete it. Completed and archived actions are retained for 12 months before being automatically removed by the system. There is no immediate permanent deletion.",
			},
			{
				question: "Is there a limit to the number of actions I can create?",
				answer:
					"No. However, a large number of open actions can indicate that the process for reviewing and closing actions needs attention — regular reviews help keep the list manageable.",
			},
		],
		relatedPages: [
			{ label: "AI Recommendations", pageKey: "recommendations" },
			{ label: "Variance Analysis", pageKey: "variance" },
			{ label: "Dashboard", pageKey: "dashboard" },
		],
	},

	// ── REPORTS ───────────────────────────────────────────────────
	reports: {
		pageTitle: "Reports",
		description:
			"Generate, schedule, export, and build custom cash flow reports for internal and external use.",
		icon: "BarChart3",
		iconColor: "bg-cyan-500",
		functionality: {
			overview:
				"The Reports page is the full reporting centre for Cash Forecaster. It combines a published library of available reports (both system-provided templates and your own published custom reports), a personal workspace for building and iterating on custom reports, scheduling and delivery management, a snapshot history, and report-level administration — all in one place. All report labels and terminology automatically reflect any terminology customisations made in System Administration.",
			keyFeatures: [
				{
					label: "Available Reports",
					detail:
						"A searchable, filterable grid of all reports that have been published and made available to you — including system templates and any custom reports shared with your role.",
					requiredPermission: "view_reports",
				},
				{
					label: "Favourites",
					detail:
						"Pin reports you use regularly to a favourites strip at the top of the Available Reports tab for one-click access.",
					requiredPermission: "view_reports",
				},
				{
					label: "Custom Reports workspace",
					detail:
						"Your personal workspace for building new reports from scratch or by cloning a system template. Reports stay here in draft until you choose to publish them.",
					requiredPermission: "manage_custom_reports",
				},
				{
					label: "Custom Report Builder",
					detail:
						"A multi-step wizard to configure every aspect of a report — metrics down to individual line items, layout, branding, output formats, and role-based sharing.",
					requiredPermission: "manage_custom_reports",
				},
				{
					label: "Protected System Templates",
					detail:
						"A curated set of system-provided report templates that can be cloned as a starting point but cannot be edited or deleted.",
					requiredPermission: "manage_custom_reports",
				},
				{
					label: "Scheduled Delivery",
					detail:
						"Set up recurring automated delivery of any published report — by email, to a shared folder, or as an in-app notification — on a daily, weekly, or monthly schedule.",
				},
				{
					label: "Report History",
					detail:
						"A snapshot archive of every report that has been generated, with the exact date, format, and data it contained at the time.",
					requiredPermission: "view_reports",
				},
				{
					label: "Role Access Control",
					detail:
						"Control which roles can access each published report. A searchable role picker with tag-based role chips makes this manageable even with many roles.",
					requiredPermission: "manage_custom_reports",
				},
				{
					label: "Report Studio",
					detail:
						"A global configuration area for report branding — logo library, colour schemes, and reusable header and footer templates for paginated PDF output.",
					requiredPermission: "manage_custom_reports",
				},
				{
					label: "Admin Reports",
					detail:
						"System-level reports covering roles and permissions, data source sync history, system activity, and full audit logs for scenarios and actions — visible to system administrators only. Includes: Roles & Permissions Audit, Data Source Sync Report, System Activity Log, Scenario Workflow Audit, Scenario Discussion Audit, Actions Workflow Audit, and Actions Discussion Audit.",
				},
			],
			sectionsAndFields: [
				{
					name: "Available Reports tab",
					description:
						"Favourites strip at the top (pinned reports in card format), then a compact searchable grid of all available reports below. Search, sort, and filter by type or category. Each row has contextual action icons — generate, download, print, favourite, and a 3-dot menu for secondary options.",
				},
				{
					name: "Custom Reports tab",
					description:
						"Visible to users with the Manage Custom Reports permission only. Contains system templates (protected, lock icon, Clone to Custom only) and the My Custom Reports workspace grid. Same search, sort, and filter as Available Reports. Each row has Edit, Delete, and Publish actions. A star icon indicates reports already published to Available Reports.",
				},
				{
					name: "New Report button",
					description:
						"Located at the top of the Custom Reports tab. Opens a dropdown with two options: From Scratch (blank builder) or Clone From (pick a system template or existing custom report as a starting point).",
				},
				{
					name: "Report Builder wizard",
					description:
						"A five-step wizard: (1) Name & Formats — report name, description, output format selection; (2) Metrics — grouped metric picker with individual line items; (3) Layout — section ordering and grouping; (4) Branding — colour scheme, logo picker, header/footer template; (5) Preview & Save — live preview before saving to the Custom Reports workspace.",
				},
				{
					name: "Scheduled tab",
					description:
						"All active scheduled report deliveries — frequency, format, recipients, and next scheduled run. Edit or cancel any schedule.",
				},
				{
					name: "History tab",
					description:
						"A dated list of all previously generated report snapshots. Open or download any historical report.",
				},
				{
					name: "Manage tab",
					description:
						"Visible to users with the Manage Custom Reports permission only. Contains Role Access Control (searchable role picker with chip-based assignment per report) and Report Studio (logo library, colour presets, and header/footer templates).",
				},
				{
					name: "Role Access Control",
					description:
						"For each published report, define which roles can see it. Roles are displayed as removable chips. A search filter handles large role lists.",
				},
				{
					name: "Report Studio",
					description:
						"Upload and manage multiple logos (each report picks from the library). Set default colour schemes. Create reusable header and footer templates for PDF output.",
				},
				{
					name: "Delete validation",
					description:
						"Deleting a custom report that is linked to one or more roles is blocked with an explanation and a prompt to unlink those roles first. Once unlinked, a confirmation dialog is shown before deletion.",
				},
			],
			useCases: [
				"Generating a cash position report for a board meeting directly from the Available Reports tab.",
				"Scheduling a weekly cash summary to be emailed to the finance director every Monday morning.",
				"Building a custom executive dashboard report from scratch, configured to show only the metrics the CEO cares about.",
				"Cloning the standard Cash Flow Summary template and tweaking the metric selection for a lender presentation.",
				"Publishing a finished custom report and assigning it to the Finance Manager and CFO roles.",
				"Using the History tab to retrieve the exact report that was sent to the board six months ago.",
				"Uploading the company logo to Report Studio so it appears consistently on all PDF output.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Reports** in the sidebar. The **Available Reports** tab loads first, showing all reports published and available to you. To build your own reports, click the **Custom Reports** tab (visible if you have the Manage Custom Reports permission).",
			steps: [
				{
					title: "How do I generate a report?",
					items: [
						{ text: "Click **Reports** in the sidebar." },
						{ text: "Click the **Available Reports** tab." },
						{
							text: "Find the report in the grid — use the search bar or filters if needed.",
						},
						{
							text: "Click the generate icon on the row, or click the 3-dot menu and select **Generate**.",
						},
						{
							text: "Choose your format and click **Download**.",
							tip: "PDF is best for sharing with external stakeholders. Excel or CSV is better for further analysis in a spreadsheet.",
						},
					],
					requiredPermission: "view_reports",
				},
				{
					title: "How do I favourite a report?",
					items: [
						{ text: "Click the **Available Reports** tab." },
						{ text: "Find the report in the grid." },
						{ text: "Click the star icon on the row." },
						{
							text: "The report is pinned to the Favourites strip at the top of the screen for one-click access.",
							tip: "Favourite the 3–5 reports you use most often — the strip keeps them a single click away without scrolling.",
						},
					],
					requiredPermission: "view_reports",
				},
				{
					title: "How do I build a new custom report?",
					items: [
						{ text: "Click the **Custom Reports** tab." },
						{
							text: "Click **New Report** and choose **From Scratch** or **Clone From** a template.",
						},
						{
							text: "Step 1 — enter a report name, description, and choose your output formats. Click **Next**.",
						},
						{
							text: "Step 2 — select the individual metrics to include, organised by group. Click **Next**.",
						},
						{
							text: "Step 3 — order and group the selected metrics into sections. Click **Next**.",
						},
						{
							text: "Step 4 — choose a colour scheme, pick a logo from the library, and select a header/footer template. Click **Next**.",
						},
						{
							text: "Step 5 — review the live preview and click **Save**.",
							note: "The report is saved as a draft in your Custom Reports workspace. It is not visible to other users until you publish it.",
						},
					],
					requiredPermission: "manage_custom_reports",
				},
				{
					title: "How do I publish a custom report so others can access it?",
					items: [
						{
							text: "Click the **Custom Reports** tab and find your finished report in the My Custom Reports grid.",
						},
						{
							text: "Click the 3-dot menu on the report row and select **Add to Available Reports**.",
						},
						{
							text: "Assign the report to the relevant roles using the role picker.",
						},
						{
							text: "Click **Publish**.",
							note: "A toast message confirms the report has been published. It appears in the Available Reports tab for all users in the assigned roles.",
						},
					],
					requiredPermission: "manage_custom_reports",
				},
				{
					title: "How do I schedule a recurring report?",
					items: [
						{
							text: "Click the **Available Reports** tab and find the report you want to schedule.",
						},
						{ text: "Click the 3-dot menu and select **Schedule**." },
						{
							text: "Set the frequency — daily, weekly, or monthly — and choose the output format and recipient list.",
						},
						{
							text: "Click **Save Schedule**.",
							note: "Scheduled reports run automatically and appear in the History tab when generated. You can edit or cancel any schedule from the Scheduled tab.",
						},
					],
				},
				{
					title: "How do I delete a custom report?",
					items: [
						{ text: "Click the **Custom Reports** tab and find the report." },
						{ text: "Click the **Delete** icon on the row." },
						{
							text: "If the report is linked to any roles, a warning displays — go to the **Manage** tab to remove the role assignments first.",
						},
						{
							text: "Once no roles are linked, click **Delete** again and click **Confirm** in the dialog.",
							note: "Deletion is permanent. Consider using the Edit option to rename or archive a report rather than deleting it if you might need it later.",
						},
					],
					requiredPermission: "manage_custom_reports",
				},
				{
					title: "How do I control which roles can see a report?",
					items: [
						{ text: "Click the **Manage** tab." },
						{ text: "Find the report in the Role Access Control section." },
						{
							text: "Use the role search to find the roles you want to assign.",
						},
						{ text: "Click a role to add it as a chip on the report." },
						{
							text: "Click the × on any chip to remove a role's access.",
							note: "Changes to role access take effect immediately. Users in removed roles will no longer see the report on their Available Reports tab.",
						},
					],
					requiredPermission: "manage_custom_reports",
				},
				{
					title: "How do I set up report branding?",
					items: [
						{
							text: "Click the **Manage** tab and open the Report Studio section.",
						},
						{
							text: "Click the upload button to add one or more logos to the logo library.",
						},
						{
							text: "Set a default colour scheme, or configure a custom scheme.",
						},
						{
							text: "Create a header and footer template for PDF reports — these are reusable across all reports.",
							tip: "Set up logos and header/footer templates in Report Studio before building reports — you can then pick them in the builder rather than configuring them per report.",
						},
					],
					requiredPermission: "manage_custom_reports",
				},
			],
			proTips: [
				"Start by cloning a system template rather than building from scratch — it's much faster to remove metrics you don't need than to add them one by one.",
				"Keep custom reports in draft until they're genuinely ready — publishing half-finished reports creates confusion for other users.",
				'Use descriptive report names that include the audience or purpose, for example "Board Monthly Cash Summary" rather than just "Cash Summary".',
				"Set up logos and header/footer templates in Report Studio before building reports — you can then pick them in the builder rather than configuring them per report.",
				"Schedule your most important recurring reports rather than generating them manually each time — it removes a task from your checklist and ensures nothing gets missed.",
				"Use the History tab as an audit trail — if a stakeholder questions a number from a report sent three months ago, you can retrieve the exact snapshot.",
				"Review role access on published reports at least quarterly — if roles change or new roles are added, access assignments may need updating.",
			],
		},
		faq: [
			{
				question:
					"What is the difference between Available Reports and Custom Reports?",
				answer:
					"Available Reports is the published library — every report on that tab has been deliberately made available to you or your role. Custom Reports is a personal workspace for building and iterating on reports. A custom report only moves to Available Reports when its creator explicitly publishes it and assigns it to one or more roles.",
				requiredPermission: "view_reports",
			},
			{
				question: "Can I edit a system template report?",
				answer:
					"No. System templates are protected and cannot be edited or deleted. This ensures the standard templates are always available as a clean starting point. To customise a system template, clone it to your Custom Reports workspace — the clone is fully editable.",
				requiredPermission: "manage_custom_reports",
			},
			{
				question:
					"What happens to a published report if I edit the underlying custom report?",
				answer:
					"Editing a custom report in your workspace does not automatically update the published version. You need to re-publish the report for the changes to appear in Available Reports. This is intentional — it means you can iterate privately before pushing changes out to other users.",
				requiredPermission: "manage_custom_reports",
			},
			{
				question: "Why can't I delete a report that is assigned to roles?",
				answer:
					"If a report is assigned to one or more roles, users in those roles can currently see it on their Available Reports tab. Deleting it without warning would remove access unexpectedly. You must first go to the Manage tab and remove all role assignments from the report, then delete it.",
				requiredPermission: "manage_custom_reports",
			},
			{
				question:
					"Do report labels update automatically when I change terminology settings?",
				answer:
					"Yes. All report labels, metric names, and section headings are terminology-aware and update automatically to reflect any changes made in System Administration. You never need to manually update reports when terminology changes.",
				requiredPermission: "view_reports",
			},
			{
				question: "How do I cancel a scheduled report?",
				answer:
					"Go to the Scheduled tab, find the schedule you want to cancel, and click the edit or delete icon on that row.",
			},
			{
				question: "Who receives scheduled reports?",
				answer:
					"Scheduled reports are delivered to the email addresses entered when the schedule was configured. These can be internal or external recipients — there is no restriction to system users.",
			},
			{
				question: "How far back does report history go?",
				answer:
					"Historical report snapshots are retained based on your organisation's data retention settings. Contact your system administrator if you cannot locate a report you are looking for.",
				requiredPermission: "view_reports",
			},
			{
				question:
					"Can I share a custom report with a specific individual rather than a whole role?",
				answer:
					"Reports are shared at role level — this ensures access is consistent and manageable as people join or leave teams. To give an individual access, ensure they are assigned to a role that has access to the report. If no existing role fits, speak to your system administrator about creating an appropriate role.",
				requiredPermission: "manage_custom_reports",
			},
		],
		relatedPages: [
			{ label: "Variance Analysis", pageKey: "variance" },
			{ label: "Forecast Timeline", pageKey: "forecast-90" },
			{ label: "Dashboard", pageKey: "dashboard" },
			{
				label: "Admin — Roles & Permissions",
				pageKey: "admin-roles-permissions",
			},
		],
	},

	// ── FINANCE SETTINGS — BANK ACCOUNTS ─────────────────────────
	settings: {
		pageTitle: "Finance Settings",
		description:
			"Manage connected bank accounts, credit facilities, interest rate schedules, fund restrictions, and manually declared financing arrangements.",
		icon: "HelpCircle",
		iconColor: "bg-slate-500",
		functionality: {
			overview:
				"Finance Settings gives authorised users control over the bank account and financing configuration that underpins the cash forecast. The screen is structured into two zones: connected accounts (pulled automatically from your data sources) and manually declared financing facilities (for arrangements outside your connected systems). Keeping this information accurate ensures the forecast reflects the business's true available cash position.",
			keyFeatures: [
				{
					label: "Connected Accounts (Zone 1)",
					detail:
						"View all bank accounts connected via your data sources. Balances and drawn amounts are sourced automatically — annotate each account with credit facility details, interest rate schedules, and fund restrictions.",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "Credit Facility Types",
					detail:
						"Four facility types supported: Overdraft, Revolving Credit Facility, Invoice Finance, and Asset Finance. Each type shows the fields relevant to that arrangement — no irrelevant fields cluttering the form.",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "Interest Rate Schedule",
					detail:
						"Record dated interest rate entries for each facility so the forecast correctly projects interest costs as rates change over time — not just today's rate applied forever.",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "RCF Drawdown Schedule",
					detail:
						"For Revolving Credit Facilities, record past drawdowns and plan future ones. Future-dated drawdowns feed directly into the forecast as declared structural cash inflows. No reconciliation to the facility limit is required — enter what you know.",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "Invoice Finance Type",
					detail:
						"Specify whether a facility is Invoice Discounting (you collect — customer payment behaviour applies normally) or Invoice Factoring (the finance company collects — their settlement schedule drives forecast timing instead).",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "Restricted Funds",
					detail:
						"Declare portions of an account balance that are not freely available — client money, fixed-term deposits, notice accounts, escrow, minimum balance requirements, or internal reserves. Restricted amounts are excluded from available cash.",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "Manually Declared Financing Facilities (Zone 2)",
					detail:
						"Declare financing arrangements that are not represented in your connected data sources — such as invoice finance facilities, standalone loans, or equity investments. A clear disclaimer prevents accidental duplication of connected arrangements.",
					requiredPermission: "manage_bank_account_details",
				},
				{
					label: "Include in Available Cash Toggle",
					detail:
						"For each facility, choose whether the headroom counts as available funds in the forecast or is held as emergency reserve only.",
					requiredPermission: "manage_bank_account_details",
				},
			],
			sectionsAndFields: [
				{
					name: "Zone 1 — Connected Accounts",
					description:
						"Accounts connected via data sources. Balances and drawn amounts are system-derived. Users annotate with facility and restriction details.",
				},
				{
					name: "Zone 2 — Manually Declared Facilities",
					description:
						"Financing arrangements outside connected systems. All fields including drawn amounts are user-maintained.",
				},
				{
					name: "Credit Facility — Overdraft",
					description:
						"Limit, drawn (derived from feed), headroom (computed), include/exclude toggle, interest rate schedule, review/expiry date, security note.",
				},
				{
					name: "Credit Facility — RCF",
					description:
						"Limit, drawn, headroom, availability period start/end, commitment fee %, interest rate schedule, drawdown schedule.",
				},
				{
					name: "Credit Facility — Invoice Finance",
					description:
						"Type (discounting/factoring), funding line ceiling, advance rate, concentration limit, discount charge, service fee, eligible debtor criteria, factor settlement schedule (factoring only), interest rate schedule.",
				},
				{
					name: "Credit Facility — Asset Finance",
					description:
						"Type (hire purchase/finance lease), asset description, asset value, start date, term, end date (computed), repayment amount, frequency, balloon payment (HP), residual value (lease), interest rate schedule.",
				},
				{
					name: "Restricted Funds",
					description:
						"Six types: Client Money, Fixed Term Deposit, Notice Account, Escrow, Minimum Balance, Internal Reserve. Legally mandated flag, release dates, notice periods where applicable.",
				},
				{
					name: "Available Cash Summary",
					description:
						"Per-account and organisation-level: ledger balance minus restricted plus facility headroom (where included).",
				},
			],
			useCases: [
				"Configuring an overdraft limit so the forecast correctly reflects available headroom beyond the current balance.",
				"Recording an invoice discounting facility that is not directly connected as a data source.",
				"Adding future drawdowns on a revolving credit facility so the forecast projects the incoming cash on the right dates.",
				"Marking client money as a legally protected restriction so it is always excluded from available cash.",
				"Setting up an asset finance agreement so repayments project automatically as regular forecast outflows.",
				'Switching a facility from "included in available cash" to "emergency reserve" to reflect a change in how the business treats that headroom.',
			],
		},
		userGuide: {
			gettingStarted:
				"Navigate to Finance Settings from the sidebar. The Bank Accounts screen loads showing all connected accounts in Zone 1 and any manually declared financing facilities in Zone 2.",
			steps: [
				{
					title: "How do I configure a credit facility on a connected account?",
					items: [
						{ text: "Find the account in the **Connected Accounts** section." },
						{ text: "Click **Credit facility** to expand the section." },
						{ text: "If no facility exists, click **Add facility**." },
						{
							text: "Select the **Facility type** — Overdraft, Revolving Credit Facility, Invoice Finance, or Asset Finance.",
						},
						{
							text: "Complete the fields shown — they change based on the type selected.",
						},
						{
							text: "Click **Save changes**.",
							note: "The facility headroom is added to available cash immediately unless you set it to emergency reserve only.",
						},
					],
					requiredPermission: "manage_bank_account_details",
				},
				{
					title: "How do I add an interest rate schedule?",
					items: [
						{ text: "Open the credit facility on the relevant account." },
						{ text: "Scroll to the **Interest rate schedule** section." },
						{ text: "Click **Add rate**." },
						{
							text: "Enter the **Effective from** date, the **Rate (%)**, and an optional note.",
						},
						{ text: "Add further entries for any known future rate changes." },
						{
							text: "Click **Save changes**.",
							tip: "Enter historical rates too — the forecast uses the rate that was current on each future date, giving more accurate interest cost projections across the full horizon.",
						},
					],
					requiredPermission: "manage_bank_account_details",
				},
				{
					title: "How do I record future drawdowns on an RCF?",
					items: [
						{
							text: "Open the Revolving Credit Facility on the relevant account.",
						},
						{ text: "Scroll to the **Drawdown schedule** section." },
						{ text: "Click **Add drawdown**." },
						{
							text: "Enter the planned date, amount, and an optional description.",
						},
						{
							text: "Click **Save changes**.",
							note: "Future-dated drawdowns feed directly into the forecast as planned structural cash inflows. You do not need to account for every planned drawdown — enter what you know.",
						},
					],
					requiredPermission: "manage_bank_account_details",
				},
				{
					title: "How do I add a restricted fund to an account?",
					items: [
						{
							text: "Find the account and click **Restricted funds** to expand the section.",
						},
						{ text: "Click **Add restriction**." },
						{
							text: "Select the **Restriction type** and enter the **Restricted amount** and a **Label**.",
						},
						{
							text: "If the restriction is legally mandated (e.g. client money or escrow), tick **Legally mandated restriction**.",
						},
						{ text: "Add a release date or notice period where applicable." },
						{
							text: "Click **Save restriction**, then **Save changes**.",
							note: "The restricted amount is excluded from available cash immediately.",
						},
					],
					requiredPermission: "manage_bank_account_details",
				},
				{
					title: "How do I declare a financing facility that is not connected?",
					items: [
						{
							text: "Scroll down to the **Manually Declared Financing Facilities** section.",
						},
						{
							text: "Read the disclaimer carefully — only add facilities here if they are not already in your connected data sources.",
						},
						{ text: "Click **Add facility**." },
						{
							text: "Enter a name, select the facility type, and complete the relevant fields.",
						},
						{
							text: "Click **Save changes**.",
							tip: "Use this section for invoice finance facilities, standalone loans, equity investments, or any arrangement your connected sources cannot represent.",
						},
					],
					requiredPermission: "manage_bank_account_details",
				},
			],
			proTips: [
				"The drawn amount on Zone 1 overdrafts is derived automatically from the bank feed — you only need to maintain the limit and other configuration fields.",
				"For invoice discounting vs factoring — the distinction matters to the forecast. Discounting means customer payment behaviour applies; factoring means it doesn't.",
				"Add at least one interest rate entry for every facility — without it, the forecast cannot project interest costs accurately.",
				"If you're unsure whether a financing arrangement is already represented in a connected source, check with your system administrator before adding it to Zone 2.",
			],
		},
		faq: [
			{
				question: "Can I add a new bank account?",
				answer:
					"New bank accounts are added by connecting a new data source through Admin → Data Sources. Contact your system administrator to add a new account connection. The Bank Accounts screen only shows accounts already connected.",
				requiredPermission: "manage_bank_account_details",
			},
			{
				question: "What is the difference between Zone 1 and Zone 2?",
				answer:
					"Zone 1 (Connected Accounts) shows accounts pulled automatically from your data sources — balances are system-derived. Zone 2 (Manually Declared Facilities) is for financing arrangements outside your connected systems where all fields including drawn amounts are entered manually.",
				requiredPermission: "manage_bank_account_details",
			},
			{
				question:
					"What is the difference between invoice discounting and factoring?",
				answer:
					"With invoice discounting, you continue to collect from your customers as normal — customer payment behaviour applies in the forecast. With factoring, the finance company collects on your behalf — your customers' payment habits no longer drive the forecast timing, the factor's settlement schedule does instead.",
				requiredPermission: "manage_bank_account_details",
			},
			{
				question: "Do I need to enter every planned RCF drawdown?",
				answer:
					"No. The drawdown schedule does not need to reconcile to the facility limit. Enter the drawdowns you know about — planned entries will appear in the forecast, unknown future drawdowns will simply be absent. This is the correct approach.",
				requiredPermission: "manage_bank_account_details",
			},
			{
				question: 'What does "include in available cash" mean?',
				answer:
					"When ticked, the facility's undrawn headroom is counted as available funds in the forecast and dashboards. When unticked, the facility is treated as emergency reserve only and not included in available cash calculations.",
				requiredPermission: "manage_bank_account_details",
			},
			{
				question:
					"What happens if I accidentally duplicate a connected facility in Zone 2?",
				answer:
					"It will overstate available cash and distort the forecast. Remove the duplicate from Zone 2 immediately. The Zone 2 disclaimer is designed to prevent this — read it carefully before adding a manually declared facility.",
				requiredPermission: "manage_bank_account_details",
			},
		],
		relatedPages: [
			{ label: "Dashboard", pageKey: "dashboard" },
			{ label: "Transactions", pageKey: "transactions" },
		],
	},

	// ── MY PREFERENCES ────────────────────────────────────────────
	preferences: {
		pageTitle: "My Preferences",
		description:
			"Configure your personal display settings, alert thresholds, and notification preferences.",
		icon: "HelpCircle",
		iconColor: "bg-gray-500",
		functionality: {
			overview:
				"My Preferences lets you personalise how Cash Forecaster works for you — without affecting any other user. You can set your preferred number display format, configure the alerts and thresholds that matter to your role, control which notifications you receive, and adjust forecasting display options to suit your working style.",
			keyFeatures: [
				{
					label: "Display Precision",
					detail:
						"Choose how currency amounts are displayed — full value with pence, rounded to nearest pound, abbreviated (£1.2M), or in millions (£1.23M).",
				},
				{
					label: "Forecast Alerts",
					detail:
						"Set personal thresholds for when the forecast should alert you — for example, when the projected cash balance falls below a specific amount.",
				},
				{
					label: "Chart Health Zones",
					detail:
						"Control how health zone bands (healthy / caution / risk) appear on each forecast chart. Choose Off, Line only, or Full Bands independently for the Dashboard 90-Day chart, the Forecast 90-Day view, the 12-Month view, and the 3-Year view. You can also set your personal boundary values for the healthy and caution thresholds.",
				},
				{
					label: "Transaction Alerts",
					detail:
						"Configure alerts for individual transactions — such as when a single payment exceeds a threshold you define.",
				},
				{
					label: "Variance Alerts",
					detail:
						"Set thresholds for variance notifications — so you are only alerted when a variance is material to your role.",
				},
				{
					label: "Notifications",
					detail:
						"Control which in-app feed notifications you receive. Notifications are organised into collapsible sections — Data Sources, Scenarios, Forecasting, Actions, Recurring Transactions, Reports, AI Recommendations, Users, Roles & Permissions, User Defaults, Category Mapping, General Settings, Terminology, and Cash Position. Each section has a Select all / Deselect all control. Some notifications are locked on (shown with a lock icon) because they represent essential system events — these cannot be turned off.",
				},
				{
					label: "Forecasting Display",
					detail:
						"Adjust the default time period and view shown when you open forecast pages.",
				},
				{
					label: "Display Settings",
					detail: "Set a preferred landing page and other display defaults.",
				},
			],
			sectionsAndFields: [
				{
					name: "Display Precision Tab",
					description:
						"Choose your preferred number format — Full, Rounded, Abbreviated, or Millions.",
				},
				{
					name: "Forecast Alerts Tab",
					description:
						"Set cash balance thresholds that trigger alerts on your dashboard, and configure chart health zone display and boundary values.",
				},
				{
					name: "Transaction Alerts Tab",
					description: "Configure alerts for individual large transactions.",
				},
				{
					name: "Variance Alerts Tab",
					description:
						"Set variance thresholds above which you want to be notified.",
				},
				{
					name: "Notifications Tab",
					description:
						"Control which in-app feed notifications you receive. Notifications are grouped into collapsible sections by area. Locked notifications (shown with a lock icon and greyed toggle) are always active — they represent essential system events that cannot be turned off. Use Select all / Deselect all within each section to manage toggleable items in bulk. Sections are permission-filtered — you only see sections relevant to your role.",
				},
				{
					name: "Forecasting Tab",
					description:
						"Set your preferred default 90-day view (7 Days, 30 Days, or 90 Days) — which period is pre-selected when you open the 90-Day Forecast screen.",
				},
				{
					name: "Display Tab",
					description:
						"Set a default landing page and other personalisation options.",
				},
			],
			useCases: [
				"A CFO switching to abbreviated display (£1.2M) so large numbers are easier to read quickly.",
				"An accounts assistant setting full precision (£1,234.56) for detailed transaction review.",
				"Setting a personal forecast alert threshold lower than the company default to get earlier warning.",
				"Turning off the chart health zone bands on the 12-Month and 3-Year charts to reduce visual clutter, while keeping Full Bands on the 90-Day chart where short-term risk is most relevant.",
				"Setting personal healthy and caution threshold values that reflect the cash position relevant to your role.",
				"Turning off low-priority notifications to reduce noise during busy periods.",
				"Setting the default landing page to the Forecast Timeline so it opens immediately on login.",
			],
		},
		userGuide: {
			gettingStarted:
				"Navigate to My Preferences from your user avatar or the bottom of the sidebar. Changes take effect immediately and only apply to your account.",
			steps: [
				{
					title: "How do I change how numbers are displayed?",
					items: [
						{ text: "Go to the Display Precision tab." },
						{
							text: "Select your preferred format — Full, Rounded, Abbreviated, or Millions.",
						},
						{
							text: "Click Save. The change applies immediately across all pages.",
							tip: "Full precision (£1,234.56) is best for detailed work. Abbreviated (£1.2M) is useful for executive-level overview screens.",
						},
					],
				},
				{
					title: "How do I set a personal cash alert threshold?",
					items: [
						{ text: "Go to the Forecast Alerts tab." },
						{
							text: "Enter the cash balance amount at which you want to be alerted.",
						},
						{
							text: "Click Save.",
							note: "Your personal threshold supplements the company-level thresholds set by the system administrator — it does not replace them. Alert notifications are independent of the chart display settings below.",
						},
					],
				},
				{
					title: "How do I control the health zone display on forecast charts?",
					items: [
						{ text: "Go to the **Forecast Alerts** tab." },
						{ text: "Scroll to the **Chart Health Zones** section." },
						{
							text: "Optionally set your personal **Healthy threshold** and **Caution threshold** values. Leave blank to use the company defaults set by your administrator.",
						},
						{
							text: "For each chart — Dashboard 90-Day, Forecast 90-Day, 12-Month, and 3-Year — select your preferred display mode from the dropdown: **Off**, **Line only**, or **Full Bands**.",
						},
						{
							text: "Click **Save Changes**.",
							note: "Off removes all zone indicators from that chart. Line only shows just the caution threshold as a dashed line. Full Bands shows colour-coded zones across the entire chart area. Changes apply only to your view — other users are not affected. Alert notifications are always active regardless of the display setting you choose here.",
						},
					],
				},
				{
					title: "How do I manage my notification preferences?",
					items: [
						{ text: "Click the **Notifications** tab in My Preferences." },
						{
							text: "Review the collapsible sections — each section covers a different area of the product (Data Sources, Forecasting, Actions, and so on).",
						},
						{
							text: "Click a section header to expand it and see the individual notifications inside.",
						},
						{
							text: "Toggle individual notifications on or off using the switch on each row.",
						},
						{
							text: "Use the **Select all** or **Deselect all** link at the top of each section to manage all toggleable items in that section at once.",
						},
						{
							text: "Click **Save** to apply your changes.",
							note: "Some notifications have a lock icon and a greyed-out toggle — these are always on and cannot be turned off. They cover essential system events such as data source disconnections, auth token expiry, and security-related changes.",
						},
						{
							text: "You will only see sections that are relevant to your permissions — sections covering features outside your role are hidden entirely.",
							tip: "If you are receiving too many notifications, start by collapsing each section and using Deselect all to turn off whole categories, then re-enable only the ones you care about.",
						},
					],
				},
				{
					title: "How do I set my default 90-Day forecast view?",
					items: [
						{ text: "Click the **Forecasting** tab in My Preferences." },
						{
							text: "Under **Default 90-Day View**, select **7 Days**, **30 Days**, or **90 Days**.",
						},
						{
							text: "Click **Save Changes**.",
							note: "This controls which period button is pre-selected when you open the 90-Day Forecast screen. It does not affect which forecast page you land on — that is controlled separately by the Default Landing Page setting in the Display tab.",
						},
					],
				},
				{
					title: "How do I change my default landing page?",
					items: [
						{ text: "Click the **Display** tab in My Preferences." },
						{ text: "Select your preferred landing page from the dropdown." },
						{
							text: "Click **Save**. The next time you log in, you will land on that page automatically.",
						},
					],
				},
			],
			proTips: [
				"Display precision is a personal preference — choose whatever format helps you read numbers most quickly and accurately for your role.",
				"Set variance alert thresholds at a level that is material to your decisions. Too low and you get noise; too high and you miss things.",
				"If you find yourself navigating to the same page every time you log in, set it as your landing page.",
				"Preferences are personal — changes you make here do not affect any other user's view.",
			],
		},
		faq: [
			{
				question: "Do my preferences affect other users?",
				answer:
					"No. Every setting in My Preferences applies only to your account. Other users see the same data but through their own display preferences.",
			},
			{
				question:
					"If I turn off the health zone bands on a chart, will I still receive alerts?",
				answer:
					"Yes. Alert notifications are completely separate from chart display settings. Turning off the health zone display on a chart only changes what you see on the chart — it has no effect on dashboard alerts or notifications. Your alert threshold is managed in the Forecast Alerts section and is always active.",
			},
			{
				question:
					"What is the difference between the three chart health zone display modes?",
				answer:
					"Off removes all zone indicators from that chart — a clean chart with the forecast line only. Line only shows a single dashed threshold line at the caution boundary — minimal but still marks the critical level. Full Bands shows colour-coded zones across the full chart area — green above the healthy threshold, amber between healthy and caution, red below the caution threshold.",
			},
			{
				question: "Will my preferences be saved if I log out?",
				answer:
					"Yes. Preferences are saved to your account and persist between sessions. They will be applied automatically the next time you log in.",
			},
			{
				question: "Can my system administrator override my preferences?",
				answer:
					"System administrators control role-level defaults. If your administrator updates the default settings for your role, those changes will apply unless you have already set a personal override.",
			},
			{
				question:
					"What is the difference between Abbreviated and Millions display?",
				answer:
					"Abbreviated format shows one decimal place (£1.2M, £450K). Millions format shows two decimal places in millions only (£1.23M). Abbreviated is generally easier to read at a glance; Millions is more precise for financial reporting contexts.",
			},
		],
		relatedPages: [{ label: "Dashboard", pageKey: "dashboard" }],
	},

	// ── ADMIN — DASHBOARD ─────────────────────────────────────────
	"admin-dashboard": {
		pageTitle: "Admin Dashboard",
		description:
			"A system-level overview of data source status, user activity, and recent configuration changes.",
		icon: "LayoutDashboard",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"The Admin Dashboard gives system administrators a quick health check of the Cash Forecaster environment — data source connectivity, last sync times, user activity, and any system alerts. It is the starting point for administrative work and provides direct links to the main configuration areas.",
			keyFeatures: [
				{
					label: "Quick Actions",
					detail:
						"Direct links to the most common administrative tasks — manage users, configure data sources, and access system settings.",
					requiredPermission: "manage_users",
				},
				{
					label: "System Status",
					detail:
						"An at-a-glance health indicator for the system — confirming all components are running correctly.",
					requiredPermission: "manage_general_settings",
				},
			],
			sectionsAndFields: [
				{
					name: "Data Sources Card",
					description:
						"Shows the number of connected sources, their sync status, and the time of the last successful sync.",
				},
				{
					name: "Recent Activity Card",
					description:
						"In live mode, displays the last 10 data source sync completions with the data source name, who triggered the sync (user name or System for scheduled syncs), the date and time of the sync, and a relative timestamp. In demo mode, shows sample activity entries.",
				},
				{
					name: "Quick Actions Card",
					description:
						"Buttons linking directly to Users, Data Sources, and System Settings.",
				},
			],
			useCases: [
				"Checking first thing in the morning whether all data sources synced successfully overnight.",
				"Reviewing recent activity after a user reports an unexpected change to confirm what happened.",
				"Using Quick Actions to jump directly to user management without navigating through the full menu.",
			],
		},
		userGuide: {
			gettingStarted:
				"The Admin Dashboard is the default landing page for users with the System Administrator role. It loads automatically on login.",
			steps: [
				{
					title: "How do I check if all data sources are syncing correctly?",
					items: [
						{
							text: "Review the **Data Sources** card on the Admin Dashboard.",
						},
						{
							text: "Check the sync status and last sync time shown for each connected source.",
						},
						{
							text: "If any source shows an error status, click **Data Sources** in the Quick Actions card to investigate.",
							note: "A failed sync means the forecast may not reflect the most recent data. Investigate and resolve sync errors promptly.",
						},
					],
				},
				{
					title: "How do I review recent system activity?",
					items: [
						{
							text: "Review the **Recent Activity** card on the Admin Dashboard.",
						},
						{
							text: "Scroll through the list to see events with timestamps and the user who performed each action.",
						},
						{
							text: "Click **Activity Log** in the left sidebar for the full searchable audit trail.",
						},
					],
				},
				{
					title: "How do I use Quick Actions?",
					items: [
						{
							text: "Click **Manage Users** in the Quick Actions card to go directly to the User Management screen.",
						},
						{
							text: "Click **Data Sources** in the Quick Actions card to go directly to the Data Sources screen.",
						},
						{
							text: "Click **System Settings** in the Quick Actions card to go directly to the General Settings screen.",
						},
					],
				},
			],
			proTips: [
				"Check the admin dashboard at the start of each working day — a failed overnight sync is the most common issue that affects forecast accuracy.",
				"Use the Recent Activity log to investigate any unexpected changes — it provides a full audit trail of who changed what and when.",
			],
		},
		faq: [
			{
				question: "Who can access the Admin Dashboard?",
				answer:
					"Only users with the System Administrator role or equivalent administrative permissions can access the Admin section. Financial users do not see the Admin navigation items.",
				requiredPermission: "manage_general_settings",
			},
			{
				question: "What do I do if a data source shows a sync error?",
				answer:
					"Navigate to Admin → Data Sources and open the affected source. Review the error message and check the connection credentials and endpoint settings. If the issue persists, contact your Access Group support team.",
			},
		],
		relatedPages: [
			{ label: "User Management", pageKey: "admin-users" },
			{ label: "Data Sources", pageKey: "admin-data-sources" },
			{ label: "General Settings", pageKey: "admin-general-settings" },
		],
	},

	// ── ADMIN — USER MANAGEMENT ───────────────────────────────────
	"admin-users": {
		pageTitle: "User Management",
		description:
			"Add, edit, and manage user accounts and their role assignments.",
		icon: "HelpCircle",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"User Management is where system administrators control who has access to Cash Forecaster and what they can do. Users are created here, assigned to roles, and deactivated when they leave the organisation. Role assignment is the primary mechanism for controlling what each user can see and do — permissions follow from roles, not from individual user configuration.",
			keyFeatures: [
				{
					label: "User List",
					detail:
						"View all user accounts with their name, email, role, and current status.",
					requiredPermission: "manage_users",
				},
				{
					label: "Invite User",
					detail: "Create a new user account and send an invitation email.",
					requiredPermission: "manage_users",
				},
				{
					label: "Edit User",
					detail: "Update a user's name, email, or role assignment.",
					requiredPermission: "manage_users",
				},
				{
					label: "Deactivate User",
					detail:
						"Disable a user's access without deleting their account or historical data.",
					requiredPermission: "manage_users",
				},
				{
					label: "Role Assignment",
					detail:
						"Assign a user to a role to give them the permissions defined for that role.",
					requiredPermission: "manage_roles_permissions",
				},
			],
			sectionsAndFields: [
				{
					name: "User Table",
					description:
						"Lists all users with name, email, assigned role, last active date, and status.",
				},
				{
					name: "Role Column",
					description:
						"Shows the role currently assigned to each user. Click to change the role assignment.",
				},
				{
					name: "Status Badge",
					description:
						"Active or Inactive — controls whether the user can log in.",
				},
				{
					name: "Invite Button",
					description:
						"Opens the form to create a new user account and send an invitation.",
				},
			],
			useCases: [
				"Adding a new finance team member and assigning them to the Finance Manager role.",
				"Changing a user's role after a promotion or change in responsibilities.",
				"Deactivating an account when an employee leaves the organisation.",
				"Reviewing who has access to the system and what role each person holds.",
			],
		},
		userGuide: {
			gettingStarted:
				"Navigate to Admin → Users from the sidebar. The user list loads showing all current accounts.",
			steps: [
				{
					title: "How do I invite a new user?",
					items: [
						{ text: "Click **Invite User** at the top of the user list." },
						{
							text: "Enter the user's name and email address in the fields provided.",
						},
						{ text: "Select a role from the **Role** dropdown." },
						{
							text: "Click **Send Invitation**.",
							note: "The user will receive an email with a link to set their password and activate their account.",
						},
					],
					requiredPermission: "manage_users",
				},
				{
					title: "How do I edit a user's details or role?",
					items: [
						{
							text: "Find the user in the list and click their row to open the edit panel.",
							note: "The edit panel automatically opens from the right-hand side of the screen. You can close the panel by clicking the arrow icon on the right-hand side middle of the screen.",
						},
						{
							text: "Update the fields required — name, email, or role assignment.",
						},
						{
							text: "Click **Save**.",
							note: "A role change takes effect immediately. The user will see a different set of pages and permissions the next time they navigate or refresh.",
						},
					],
					requiredPermission: "manage_roles_permissions",
				},
				{
					title: "How do I deactivate a user?",
					items: [
						{
							text: "Find the user in the list and click their row to open the edit panel.",
						},
						{ text: "Click the **Deactivate** button." },
						{
							text: "Click **Confirm**.",
							note: "Deactivating a user prevents them from logging in but preserves all their historical data and activity log entries.",
						},
					],
					requiredPermission: "manage_users",
				},
				{
					title: "How do I reactivate a user?",
					items: [
						{
							text: "Find the inactive user in the list.",
							note: "Use the status filter to show inactive accounts if they are not visible in the default view.",
						},
						{ text: "Click their row to open the edit panel." },
						{ text: "Click **Activate** then click **Confirm**." },
					],
					requiredPermission: "manage_users",
				},
			],
			proTips: [
				"Always assign the least permissive role that meets the user's needs — it is easier to grant more access later than to explain why someone had access they should not have had.",
				"Review the user list quarterly to check for accounts that should be deactivated — particularly for staff who have moved roles or left the organisation.",
				"Deactivate accounts on the same day someone leaves — do not wait for an admin cycle.",
			],
		},
		faq: [
			{
				question: "Can I delete a user account?",
				answer:
					"Accounts are deactivated rather than deleted, to preserve the audit trail of actions and changes made by that user. Deleted accounts would create gaps in the activity log.",
				requiredPermission: "manage_users",
			},
			{
				question:
					"What happens to a user's actions and data when they are deactivated?",
				answer:
					"All data created by the user — actions, comments, scenario contributions — is retained. Actions assigned to the deactivated user remain open and can be reassigned.",
				requiredPermission: "manage_users",
			},
			{
				question: "Can I assign a user to more than one role?",
				answer:
					"Each user is assigned to one role at a time. If a user needs permissions from multiple roles, either create a custom role that combines the required permissions, or assign them to the role that best matches their primary responsibilities.",
				requiredPermission: "manage_roles_permissions",
			},
		],
		relatedPages: [
			{ label: "Roles & Permissions", pageKey: "admin-roles-permissions" },
			{ label: "User Defaults", pageKey: "admin-user-defaults" },
			{ label: "Activity Log", pageKey: "admin-activity-log" },
		],
	},

	// ── ADMIN — ROLES & PERMISSIONS ───────────────────────────────
	"admin-roles-permissions": {
		pageTitle: "Roles & Permissions",
		description:
			"Define and manage roles and the permissions assigned to each role.",
		icon: "HelpCircle",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				'Roles and Permissions is where the access model for your organisation is defined. A role is a named set of permissions — for example, "Finance Manager" with access to transactions, forecasts, and reports. Every user is assigned to a role, and their permissions come entirely from that role. You can create as many custom roles as your organisation needs.',
			keyFeatures: [
				{
					label: "Role List",
					detail:
						"View all defined roles with a description and a count of users currently assigned to each.",
					requiredPermission: "manage_roles_permissions",
				},
				{
					label: "Create Role",
					detail:
						"Define a new role with a name, description, and a custom set of permissions.",
					requiredPermission: "manage_roles_permissions",
				},
				{
					label: "Edit Permissions",
					detail: "Add or remove individual permissions from an existing role.",
					requiredPermission: "manage_roles_permissions",
				},
				{
					label: "Delete Role",
					detail:
						"Remove a role that is no longer needed — only possible if no users are assigned to it.",
					requiredPermission: "manage_roles_permissions",
				},
				{
					label: "Permission Groups",
					detail:
						"Permissions are organised into logical groups — Forecasting, Transactions, Scenarios, Actions, Reports, Admin — to make configuration manageable.",
					requiredPermission: "manage_roles_permissions",
				},
			],
			sectionsAndFields: [
				{
					name: "Role Card",
					description:
						"Each role is shown with its name, description, user count, and an edit button.",
				},
				{
					name: "Permission Groups",
					description:
						"Expandable sections grouping related permissions together — Forecasting, Transactions, Scenarios, Actions, Reports, System Settings, Admin.",
				},
				{
					name: "Permission Toggle",
					description:
						"A switch for each permission — on means the role has that permission, off means it does not.",
				},
				{
					name: "Protected Badge",
					description:
						"Some system roles (System Administrator) are protected and cannot be deleted, though their permissions can be adjusted.",
				},
			],
			useCases: [
				'Creating a new "Senior Analyst" role with read access to all financial data but no editing permissions.',
				"Adjusting the Finance Manager role to include scenario approval rights after a process change.",
				"Removing reporting permissions from a role after a restructure that centralised report generation.",
				"Reviewing the full permission set of a role before assigning a new user to it.",
			],
		},
		userGuide: {
			gettingStarted:
				"Navigate to Admin → Roles & Permissions. The role list shows all current roles. Click any role to view and edit its permissions.",
			steps: [
				{
					title: "How do I create a new role?",
					items: [
						{ text: "Click **New Role** at the top of the roles list." },
						{
							text: "Enter a name and description for the role in the fields provided.",
						},
						{
							text: "Expand each permission group and toggle on the permissions this role should have.",
						},
						{
							text: "Click **Save**.",
							tip: "Start with the minimum permissions needed and add more as required — it is easier to grant access than to explain why it was removed.",
						},
					],
					requiredPermission: "manage_roles_permissions",
				},
				{
					title: "How do I edit the permissions on an existing role?",
					items: [
						{ text: "Click on the role card to open it." },
						{
							text: "Expand the permission groups and toggle permissions on or off as required.",
						},
						{
							text: "Click **Save**.",
							note: "Changes take effect immediately for all users assigned to that role. Users will see different navigation items and page content the next time they navigate or refresh.",
						},
					],
					requiredPermission: "manage_roles_permissions",
				},
				{
					title: "How do I delete a role?",
					items: [
						{ text: "Click on the role card to open it." },
						{ text: "Click the **Delete** button." },
						{
							text: "Click **Confirm**.",
							note: "A role can only be deleted if no users are currently assigned to it. Reassign all users to a different role before attempting to delete.",
						},
					],
					requiredPermission: "manage_roles_permissions",
				},
			],
			proTips: [
				"Use role descriptions to document the intended audience for each role — it makes future maintenance much easier.",
				"Before deleting a role, check whether any of its permissions should be preserved in a different role.",
				"Keep the number of roles manageable — too many roles become hard to maintain. Aim for one role per distinct set of responsibilities rather than one per person.",
				"Review the System Administrator role carefully — it is the most powerful role in the system and should have the fewest assigned users.",
			],
		},
		faq: [
			{
				question: "What happens to users if I change a role's permissions?",
				answer:
					"Changes to a role's permissions apply immediately to all users assigned to that role. Users will see different navigation items and page content the next time they navigate or refresh.",
				requiredPermission: "manage_roles_permissions",
			},
			{
				question: "Can I delete a role that has users assigned to it?",
				answer:
					"No. You must reassign all users to a different role before a role can be deleted. This prevents accidental loss of access for active users.",
				requiredPermission: "manage_roles_permissions",
			},
			{
				question: "What are protected roles?",
				answer:
					"The System Administrator role is protected — it cannot be deleted because doing so could leave the system without anyone who can manage access. Its permissions can be adjusted, but the role itself always exists.",
				requiredPermission: "manage_roles_permissions",
			},
		],
		relatedPages: [
			{ label: "User Management", pageKey: "admin-users" },
			{ label: "User Defaults", pageKey: "admin-user-defaults" },
			{ label: "Dashboard Layout", pageKey: "admin-dashboard-layout" },
		],
	},

	// ── ADMIN — GENERAL SETTINGS ──────────────────────────────────
	"admin-general-settings": {
		pageTitle: "General Settings",
		description:
			"System-wide configuration including financial year, thresholds, forecasting parameters, and notification defaults.",
		icon: "HelpCircle",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"General Settings contains the organisation-wide configuration that affects how the entire Cash Forecaster system behaves. This includes the financial year definition, cash threshold levels, the forecast refresh schedule, and notification settings. Changes here affect all users — they are global settings, not per-user preferences.",
			keyFeatures: [
				{
					label: "Financial Year",
					detail:
						"Define the start and end of the financial year so reporting periods, variance analysis, and year-to-date calculations align correctly.",
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Cash Thresholds",
					detail:
						"Set the organisation-level cash balance thresholds that trigger caution and risk zone indicators on forecast charts.",
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Forecast Refresh Schedule",
					detail:
						"Configure how often the full forecast recalculates. Options are Every 6 hours, Every 12 hours, or Daily. Set the days of the week and the operating window for the schedule.",
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Notification Defaults",
					detail:
						"Set default notification settings that apply to all users unless overridden in their personal preferences.",
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Dashboard Layout",
					detail:
						"Configure the default dashboard card layout for each role — the sequence and arrangement of cards that users see when they first log in.",
					requiredPermission: "manage_dashboard_layout",
				},
				{
					label: "Automatic Snapshot Horizon",
					detail:
						"Set how far forward the forecast engine projects when taking automatic snapshots. The longer the horizon, the more future periods will have a historical baseline available for variance analysis.",
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Month-End Snapshots Toggle",
					detail:
						"Enable or disable automatic month-end snapshots. Year-end and budget activation snapshots are always-on and cannot be disabled. Disabling month-end snapshots significantly reduces the frequency of automatic baselines available for variance analysis.",
					requiredPermission: "manage_general_settings",
				},
			],
			sectionsAndFields: [
				{
					name: "Organisation Tab",
					description:
						"Financial year settings and company-level configuration.",
				},
				{
					name: "Forecasting Tab",
					description: "Forecast refresh schedule and calculation parameters.",
				},
				{
					name: "Notifications Tab",
					description:
						"System-wide notification defaults and delivery settings.",
				},
				{
					name: "Cash Thresholds Tab",
					description:
						"Caution and risk threshold amounts for the forecast chart zones.",
				},
				{
					name: "Scheduling Tab",
					description: "Automated sync schedules for connected data sources.",
				},
				{
					name: "Snapshots Tab",
					description:
						"Snapshot configuration — automatic snapshot horizon and month-end toggle. Controls how the forecast engine behaves when taking automatic snapshots.",
				},
				{
					name: "Forecast Refresh Schedule",
					description:
						'Configure the automated frequency at which the full forecast recalculates. Three frequency options are available: Every 6 hours, Every 12 hours, or Daily. Choose the days of the week the schedule runs and set a start and end hour to define the operating window. A summary label shows the effective number of runs per day given your chosen frequency and window — for example "Every 6 hours between 06:00–18:00 = 3 runs/day".',
				},
				{
					name: "Automatic Snapshot Horizon",
					description:
						"A dropdown on the Snapshots tab. Sets how far forward automatic snapshots project — options are 90 days, 6 months, 12 months, or 3 years. This determines how many future periods will have a forecast baseline available for variance analysis.",
				},
				{
					name: "Month-End Automatic Snapshots Toggle",
					description:
						"On the Snapshots tab. When disabled, an amber warning appears explaining the impact on variance analysis frequency. Year-end and budget activation snapshots remain active regardless of this setting.",
				},
			],
			useCases: [
				"Updating the financial year start date after a change in accounting period.",
				"Raising the cash risk threshold after the business grows and the minimum acceptable balance increases.",
				"Turning off a system-wide notification type that is generating too much noise for all users.",
				"Setting the forecast refresh frequency to match how often the business needs an updated cash position.",
				"Setting the forecast refresh schedule to Daily if the business only needs the forecast updated overnight.",
				"Adjusting the forecast refresh window to avoid the refresh running during peak working hours.",
				"Setting the automatic snapshot horizon to 12 months so variance analysis can compare full-year forecasts against actuals.",
				"Disabling month-end snapshots for a business that uses a non-calendar fiscal period and takes manual snapshots at their own period boundaries.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Admin** then click **General Settings**. Settings are organised into tabs — review each tab to understand what is currently configured before making changes.",
			steps: [
				{
					title: "How do I update cash threshold levels?",
					items: [
						{ text: "Click **Admin** then click **General Settings**." },
						{ text: "Click the **Cash Thresholds** tab." },
						{
							text: "Update the **Caution Threshold** field (amber zone lower boundary) and the **Risk Threshold** field (red zone lower boundary).",
						},
						{
							text: "Click **Save**.",
							note: "Changes apply immediately to all forecast charts across the system for all users.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "How do I update the financial year start date?",
					items: [
						{ text: "Click **Admin** then click **General Settings**." },
						{ text: "Click the **Organisation** tab." },
						{
							text: "Update the **Financial Year Start** field to the correct month and day.",
						},
						{
							text: "Click **Save**.",
							note: "Only change this at the start of a new financial year — not during an active period. Changing mid-year affects all year-to-date calculations and period comparisons.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "How do I configure system-wide notification defaults?",
					items: [
						{ text: "Click **Admin** then click **General Settings**." },
						{ text: "Click the **Notifications** tab." },
						{ text: "Toggle each notification type on or off as required." },
						{
							text: "Click **Save**.",
							note: "These defaults apply to all users. Individual users can override notification settings in their own preferences.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "How do I configure the forecast refresh schedule?",
					items: [
						{ text: "Click **Admin** then click **General Settings**." },
						{ text: "Locate the **Forecast Refresh Schedule** section." },
						{
							text: "Select a refresh frequency — **Every 6 hours**, **Every 12 hours**, or **Daily**.",
						},
						{ text: "Select the days of the week the schedule should run." },
						{
							text: "Set the **Start Hour** and **End Hour** to define the operating window.",
						},
						{
							text: "Review the summary label — it shows the effective number of runs per day given your frequency and window.",
							tip: "If finance team members are regularly triggering manual refreshes, this is a strong signal that the scheduled frequency is too low. Increase the frequency here to reduce the need for manual intervention.",
						},
						{
							text: "Click **Save**.",
							note: "The schedule takes effect from the next scheduled window. In-progress refreshes are not interrupted by a schedule change.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "How do I configure the automatic snapshot horizon?",
					items: [
						{ text: "Click **Admin** then click **General Settings**." },
						{ text: "Click the **Snapshots** tab." },
						{
							text: "Select the **Automatic Snapshot Horizon** — choose from 90 days, 6 months, 12 months, or 3 years.",
							note: "This controls how far forward the forecast engine projects when taking automatic snapshots. A longer horizon means more future periods will have a historical baseline available for variance analysis.",
						},
						{
							text: "Click **Save Snapshot Settings**.",
							tip: "Set the horizon to at least as long as the variance analysis period you use most often. If you regularly review quarterly variance, set a minimum of 90 days. If you review annual variance, set 12 months or longer.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "How do I enable or disable month-end automatic snapshots?",
					items: [
						{ text: "Click **Admin** then click **General Settings**." },
						{ text: "Click the **Snapshots** tab." },
						{ text: "Toggle **Month-End Automatic Snapshots** on or off." },
						{
							text: "If you disable it, review the amber warning that appears — it explains the impact on variance analysis.",
							note: "Year-end and budget activation snapshots remain active regardless of this setting. Only month-end frequency is configurable. Disabling month-end snapshots means variance analysis will only have year-end baselines and any manual snapshots you take.",
						},
						{ text: "Click **Save Snapshot Settings**." },
					],
					requiredPermission: "manage_general_settings",
				},
			],
			proTips: [
				"Review general settings at least once a year — financial year dates, thresholds, and planning horizons should reflect the current state of the business.",
				"When changing cash thresholds, brief the finance team so they understand why the alert zones have shifted.",
				"Changes to general settings affect all users immediately — make changes during low-activity periods where possible.",
				"The snapshot horizon is one of the most consequential settings in the system — set it conservatively. A 12-month horizon costs very little in storage but gives you a full year of variance analysis capability.",
				"If you disable month-end snapshots, make sure someone on the finance team knows to take manual snapshots at period boundaries — otherwise variance analysis will have gaps.",
			],
		},
		faq: [
			{
				question: "Do general settings override user preferences?",
				answer:
					"System-level thresholds and financial year settings are global and cannot be overridden by users. Notification defaults are global but can be overridden by individual users in their personal preferences.",
				requiredPermission: "manage_general_settings",
			},
			{
				question:
					"What happens if I change the financial year start date mid-year?",
				answer:
					"Changing the financial year date mid-year will affect all year-to-date calculations and period comparisons. This should only be done at the start of a new financial year, not during an active period.",
				requiredPermission: "manage_general_settings",
			},
			{
				question: "What does the automatic snapshot horizon actually control?",
				answer:
					'When the system takes an automatic snapshot (at month-end, year-end, or on budget activation), the forecast engine runs forward from that date and stores predicted balance, inflow, and outflow values for every day in the horizon period. A 90-day horizon means the snapshot stores 90 days of predictions. Later, when you use Variance Analysis, those stored predictions become the "Forecast" line that is compared against what actually happened. A longer horizon means more future periods have a historical forecast baseline available.',
				requiredPermission: "manage_general_settings",
			},
			{
				question: "Can I disable year-end and budget activation snapshots?",
				answer:
					"No. Year-end and budget activation snapshots are always-on system guardrails and cannot be disabled. Only month-end automatic snapshots can be toggled off. This ensures there is always at least an annual baseline available for variance analysis even if month-end frequency is not required.",
				requiredPermission: "manage_general_settings",
			},
		],
		relatedPages: [
			{ label: "Terminology", pageKey: "admin-terminology" },
			{ label: "Dashboard Layout", pageKey: "admin-dashboard-layout" },
			{ label: "Data Sources", pageKey: "admin-data-sources" },
			{ label: "Refresh Forecast", pageKey: "forecast-refresh" },
			{ label: "Variance Analysis", pageKey: "variance" },
		],
	},

	// ── ADMIN — TERMINOLOGY ───────────────────────────────────────
	"admin-terminology": {
		pageTitle: "Terminology",
		description:
			"Customise the language used throughout Cash Forecaster to match your organisation's preferred terminology.",
		icon: "HelpCircle",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"The Terminology page allows system administrators to change the labels used for key concepts throughout the application — so the language matches your organisation's existing vocabulary. Changes apply globally to all users immediately. This does not affect any data or calculations — only the displayed labels change.",
			keyFeatures: [
				{
					label: "Forecast Term",
					detail:
						'Change "Forecast" to "Projection", "Outlook", "Budget", or a custom term. Affects: sidebar nav labels, dashboard cards, chart legends, Variance Analysis page, Scenario builder, report previews, and the Onboarding tour.',
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Cash Movement Terms",
					detail:
						'Switch between "Incoming/Outgoing" and "Receipts/Payments". Affects: 12-month and 3-year forecast chart headers and legends, Variance Analysis table columns, Scenario builder form labels, all report previews, and drill-down modals.',
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Cash Duration Term",
					detail:
						'Change "coverage" to "runway", "reserve", or "buffer". Affects: the Current Cash dashboard mini card label and the Liquidity Analysis report preview.',
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Scenarios Term",
					detail:
						'Change "Scenarios" to "What-ifs", "Plans", "Models", or a custom term. Affects: the sidebar nav label, the Scenario Comparison dashboard card title, three report template names, and the landing page selector in User Preferences and Admin User Defaults.',
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Variance Term",
					detail:
						'Change "Variance" to "Deviation", "Difference", or "Delta". Affects: the sidebar nav label ("Variance Analysis") and two report template names.',
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Actions Term",
					detail:
						'Change "Actions" to "Tasks", "To-dos", or "Follow-ups". Affects: the sidebar nav label and the Pending Actions dashboard card title.',
					requiredPermission: "manage_general_settings",
				},
				{
					label: "Live Preview",
					detail:
						"See how the navigation and key labels will look before saving the changes.",
					requiredPermission: "manage_general_settings",
				},
			],
			sectionsAndFields: [
				{
					name: "Terminology Cards",
					description:
						"One card per configurable term — each shows the current value and the available options.",
				},
				{
					name: "Custom Option",
					description:
						'Most terms include a "Custom" option where you can enter any text you prefer.',
				},
				{
					name: "Live Preview Panel",
					description:
						"Shows a preview of the navigation and key labels with your selected terminology applied — before you save.",
				},
				{
					name: "Save Button",
					description:
						"Applies all terminology changes globally and immediately.",
				},
				{
					name: "Reset to Defaults",
					description: "Returns all terms to their original system defaults.",
				},
			],
			useCases: [
				'Changing "Forecast" to "Budget" for an organisation whose finance team uses budget terminology.',
				'Switching "Incoming/Outgoing" to "Receipts/Payments" for an accountancy-trained team.',
				'Renaming "Scenarios" to "What-ifs" to make the concept more accessible to non-finance users.',
				'Using a custom term for "Actions" to match the organisation\'s existing task management language.',
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Admin** then click **Terminology**. Each card shows a configurable term with its current value and available options. Use the Live Preview panel on the right to check the effect before saving.",
			steps: [
				{
					title: "How do I change a terminology term?",
					items: [
						{ text: "Click **Admin** then click **Terminology**." },
						{ text: "Find the term card you want to update." },
						{
							text: "Select one of the preset options, or click **Custom** and type your preferred text.",
						},
						{
							text: "Check the **Live Preview** panel to see how the navigation will look with your change applied.",
						},
						{
							text: "Click **Save Changes**.",
							note: "Changes take effect immediately for all users. Terminology is a global setting — there is no per-user override.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "How do I reset all terms to defaults?",
					items: [
						{ text: "Click **Admin** then click **Terminology**." },
						{ text: "Click **Reset to Defaults**." },
						{
							text: "Click **Confirm**.",
							note: "This resets every term to its original system default in one action.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
				{
					title: "Where does each term appear after I change it?",
					items: [
						{
							text: '**Forecast term** — Sidebar navigation labels for 90-Day, 12-Month, and 3-Year views; the 30-Day Forecast dashboard mini card; the Base Forecast chart legend on all forecast charts; the "Take Forecast Snapshot" button on Variance Analysis; the Scenario builder impact preview ("90-Day Forecast", "Base Forecast"); report preview headings; and the Onboarding tour.',
						},
						{
							text: "**Cash movement terms (Incoming / Outgoing)** — Column headers and chart legends on the 12-Month and 3-Year forecast screens; table columns on the Variance Analysis screen; form labels inside the Scenario builder (investment section); column headers in all five report preview types; and drill-down modal breakdowns.",
						},
						{
							text: "**Cash duration term (coverage / runway / reserve / buffer)** — The label on the Current Cash Position dashboard mini card; the axis label in the Liquidity Analysis report preview.",
						},
						{
							text: '**Scenarios term** — The sidebar navigation label; the Scenario Comparison dashboard card title; three report template names ("Base vs Scenarios", "Scenarios Cash Impact", "Scenarios Probability Weighting"); and the default landing page selector in User Preferences and Admin → User Defaults.',
							note: "If you use a custom term keep it short — it appears inline in report template names.",
						},
						{
							text: '**Variance term** — The sidebar navigation label ("Variance Analysis"); two report template names ("Incoming Variance", "Outgoing Variance").',
						},
						{
							text: "**Actions term** — The sidebar navigation label; the Pending Actions dashboard card title.",
						},
					],
					requiredPermission: "manage_general_settings",
				},
			],
			proTips: [
				"Use the Live Preview before saving — it is much easier to spot awkward language in context than in isolation.",
				"If you use a custom term, keep it short — sidebar navigation labels have limited space and long terms will be truncated.",
				"Brief the team before changing terminology — labels change immediately for everyone, and users who aren't expecting it can be confused when the sidebar looks different.",
				"Use Reset to Defaults if a custom term causes confusion — it restores everything in one click.",
				"The Forecast term has the widest reach — it appears on charts, nav labels, the Variance Analysis page, report previews, and the Onboarding tour. Test it thoroughly in the Live Preview before saving.",
			],
		},
		faq: [
			{
				question: "Do terminology changes affect reports and exports?",
				answer:
					'Yes. Report template names, column headers in report previews, and exported column headings all use the configured terms. For example, changing "Incoming" to "Receipts" updates the column header in the Cash Flow Statement, Daily Cash Position, and Forecast Summary report previews.',
				requiredPermission: "manage_general_settings",
			},
			{
				question: "Can individual users set their own terminology?",
				answer:
					"No. Terminology is a global system setting controlled by the administrator. It applies to all users equally — there is no per-user override. This ensures everyone in the organisation sees consistent labels.",
				requiredPermission: "manage_general_settings",
			},
			{
				question: "Which term has the most impact if I change it?",
				answer:
					"The Forecast term has the widest reach — it appears in sidebar navigation, dashboard cards, chart legends, the Variance Analysis screen, the Scenario builder, all report previews, and the Onboarding tour. The Cash Movement Terms (Incoming/Outgoing) are the next most widely used, appearing across forecast charts, Variance Analysis, Scenario builder forms, and all five report preview types.",
				requiredPermission: "manage_general_settings",
			},
		],
		relatedPages: [
			{ label: "General Settings", pageKey: "admin-general-settings" },
			{ label: "Dashboard Layout", pageKey: "admin-dashboard-layout" },
		],
	},

	// ── ADMIN — DASHBOARD LAYOUT ──────────────────────────────────
	"admin-dashboard-layout": {
		pageTitle: "Dashboard Layout",
		description:
			"Configure the default order of dashboard cards for each role.",
		icon: "LayoutDashboard",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"Dashboard Layout lets system administrators define the default sequence of cards on the dashboard for each role. Users with that role will see cards in the configured order when they log in. The cards displayed are still controlled by role permissions — this page only controls the sequence of cards the user already has permission to see.",
			keyFeatures: [
				{
					label: "Role Selector",
					detail:
						"Search and select any role to configure its default dashboard layout.",
					requiredPermission: "manage_dashboard_layout",
				},
				{
					label: "Card Sequencing",
					detail:
						"Use the up and down arrows to reorder cards within each tier group.",
					requiredPermission: "manage_dashboard_layout",
				},
				{
					label: "Tier Groups",
					detail:
						"Cards are organised into Mini (small KPI tiles), Medium (half-width), and Full Width groups. Cards can only be reordered within their group — not across groups.",
					requiredPermission: "manage_dashboard_layout",
				},
				{
					label: "Save Layout",
					detail:
						"Save the configured sequence for the selected role. All users with that role see the new order immediately.",
					requiredPermission: "manage_dashboard_layout",
				},
				{
					label: "Reset to Default",
					detail: "Return a role's layout to the system default order.",
					requiredPermission: "manage_dashboard_layout",
				},
			],
			sectionsAndFields: [
				{
					name: "Role List",
					description:
						"A searchable list of all roles on the left panel. Click a role to configure its layout.",
				},
				{
					name: "Mini Cards Section",
					description:
						"Small KPI tiles — shown in a row at the top of the dashboard. Reorder within this group.",
				},
				{
					name: "Medium Cards Section",
					description:
						"Half-width cards — shown in pairs. Reorder within this group.",
				},
				{
					name: "Full Width Section",
					description:
						"Full-width cards — shown one per row. Reorder within this group.",
				},
				{
					name: "Up/Down Arrows",
					description: "Move a card up or down within its tier group.",
				},
				{
					name: "Sequence Number",
					description:
						"The current position of each card within its tier group — updates as you reorder.",
				},
				{
					name: "Unsaved Changes Indicator",
					description:
						"An amber badge appears if you have made changes that have not yet been saved.",
				},
			],
			useCases: [
				"Prioritising the cash balance card at the top of the dashboard for the Finance Manager role.",
				"Moving the Actions card higher up for a role that is primarily task-focused.",
				"Configuring a clean, minimal layout for an executive role that only needs the top-level summary.",
				"Resetting a role's layout to the system default after a period of experimentation.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Admin** then click **Dashboard Layout**. The role list appears on the left panel. Click any role to see its current card sequence on the right.",
			steps: [
				{
					title: "How do I reorder cards for a role?",
					items: [
						{ text: "Click **Admin** then click **Dashboard Layout**." },
						{
							text: "Click the role you want to configure from the left panel.",
						},
						{
							text: "Find the card you want to move in the appropriate tier section (Mini, Medium, or Full Width).",
						},
						{
							text: "Click the **up arrow** to move the card higher, or the **down arrow** to move it lower within its section.",
						},
						{
							text: "Click **Save Layout**.",
							note: "Cards can only be moved within their tier group — a Mini card cannot be moved into the Medium or Full Width sections. The sequence number updates as you reorder.",
						},
					],
					requiredPermission: "manage_dashboard_layout",
				},
				{
					title: "How do I reset a role to the default layout?",
					items: [
						{ text: "Click **Admin** then click **Dashboard Layout**." },
						{ text: "Click the role you want to reset from the left panel." },
						{ text: "Click **Reset to Default**." },
						{
							text: "Click **Confirm**.",
							note: "This removes any custom sequence and restores the system default order for that role immediately.",
						},
					],
					requiredPermission: "manage_dashboard_layout",
				},
			],
			proTips: [
				"Configure layouts for your most important roles first — Finance Manager and CFO will benefit most from a carefully ordered dashboard.",
				"The sequence that works best is usually: most time-sensitive items first, strategic items last.",
				"If users frequently comment that they have to scroll past irrelevant cards, that is a signal to reorder.",
				"This is a role-level default — in a future release, individual users will be able to personalise their own layout further.",
			],
		},
		faq: [
			{
				question: "Does changing the layout affect what cards users can see?",
				answer:
					"No. Dashboard Layout only controls the sequence of cards. Which cards are visible is controlled entirely by role permissions on the Roles & Permissions page. A card that a user does not have permission to see will not appear regardless of the layout configuration.",
				requiredPermission: "manage_dashboard_layout",
			},
			{
				question: "Will users see the new layout immediately?",
				answer:
					"Yes. Once saved, the new sequence applies to all users with that role. They will see the updated order the next time they visit the dashboard.",
				requiredPermission: "manage_dashboard_layout",
			},
			{
				question:
					"Can I configure different layouts for different users with the same role?",
				answer:
					"Not currently — layout is configured at role level and applies to all users with that role. Per-user layout personalisation is planned for a future release.",
				requiredPermission: "manage_dashboard_layout",
			},
		],
		relatedPages: [
			{ label: "Roles & Permissions", pageKey: "admin-roles-permissions" },
			{ label: "General Settings", pageKey: "admin-general-settings" },
		],
	},

	// ── ADMIN — CATEGORY MAPPING ──────────────────────────────────
	"admin-category-mapping": {
		pageTitle: "Parent Child Categories",
		description:
			"Optionally break down broad parent categories into more granular child values for cash forecasting analysis.",
		icon: "HelpCircle",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"Parent Child Categories is an optional feature. Each transaction arriving from your data sources carries a Parent Category — the classification your accounting system uses. Where that parent category is too broad for meaningful cash forecasting analysis, you can define child categories underneath it. Users can then reassign individual transactions to the appropriate child category on the Transactions screen. If your parent categories are already granular enough, you do not need to configure anything here.",
			keyFeatures: [],
			sectionsAndFields: [
				{
					name: "Category Table",
					description:
						"Lists all categories with their name, type (parent/child), parent assignment, and active status.",
				},
				{
					name: "Parent Category",
					description:
						'A top-level grouping — for example "Incoming" or "Operating Costs".',
				},
				{
					name: "Child Category",
					description:
						'A specific transaction type that sits under a parent — for example "Invoice Receipts" under "Incoming".',
				},
				{
					name: "Active Toggle",
					description:
						"Controls whether the category is available for use in transaction categorisation.",
				},
			],
			useCases: [
				"Adding a new child category for a new type of revenue stream the business has started generating.",
				"Renaming a category to better reflect the current business terminology.",
				"Deactivating a category that was used for a project that has now ended.",
				"Reviewing the full category hierarchy to ensure it matches the current chart of accounts.",
			],
		},
		userGuide: {
			gettingStarted:
				"Navigate to Admin → Parent Child Categories. This is an optional setup area — if your parent categories from your data sources are already granular enough for forecasting, you do not need to configure anything here. If you do need more detail, the category list shows all parent and child categories. Use the filters to focus on active or inactive categories.",
			steps: [
				{
					title: "How do I add a new category?",
					items: [
						{ text: 'Click "Add Category".' },
						{ text: "Enter a name and description." },
						{
							text: "Select whether it is a parent or child category. If child, select the parent it belongs to.",
						},
						{
							text: "Click Save.",
							note: "New categories are immediately available for use in transaction categorisation.",
						},
					],
				},
			],
			proTips: [
				"Keep the category hierarchy shallow — two levels (parent and child) is usually sufficient. Deep hierarchies become hard to maintain.",
				"Align categories with your chart of accounts where possible — it makes variance analysis and reporting easier to reconcile.",
				"Deactivate rather than delete categories — historical transactions reference them and deletion would cause data integrity issues.",
			],
		},
		faq: [
			{
				question: "Can I delete a category?",
				answer:
					"Categories that have transactions assigned to them cannot be deleted — this would leave those transactions uncategorised. Use the Deactivate toggle to remove a category from use without affecting historical data.",
			},
			{
				question:
					"What happens to transactions if I deactivate their category?",
				answer:
					"Existing transactions retain their category assignment even after the category is deactivated. Deactivation only prevents the category from being selected for new transactions.",
			},
		],
		relatedPages: [
			{ label: "Transactions", pageKey: "transactions" },
			{ label: "Variance Analysis", pageKey: "variance" },
			{ label: "General Settings", pageKey: "admin-general-settings" },
		],
	},

	// ── ADMIN — DATA SOURCES ──────────────────────────────────────
	"admin-data-sources": {
		pageTitle: "Data Sources",
		description:
			"Connect, configure and manage the external systems that feed data into your cash forecast.",
		icon: "Database",
		iconColor: "bg-zinc-600",
		functionality: {
			overview:
				"Data Sources is where you connect Cash Forecaster to the systems your business already uses — your finance system, payroll provider, bank feeds, and spreadsheet exports. Each connected source automatically supplies transaction data to the forecast. The more sources you connect, the more complete and accurate your forecast becomes. Adding a new source takes you straight into configuration so the whole setup journey — connection, field mapping, and sync schedule — can be completed in one go.",
			keyFeatures: [
				{
					label: "Connected Sources",
					detail:
						"View all currently connected data sources — their status, last sync time, record count, and sync schedule.",
				},
				{
					label: "Add New Data Source",
					detail:
						"Connect a new system by choosing its category (Finance, Payroll, Banking, or Excel), selecting the product, and completing the connection steps. For Excel sources, the wizard walks you through file upload, format declaration, transaction type selection, column mapping, and data validation before you save — the source is fully configured and ready to use immediately. For all other source types, you are taken directly into Configure on save.",
				},
				{
					label: "Intelligent Transaction Type Defaults",
					detail:
						"When you select a source category, the most relevant transaction types are pre-selected for you. Finance pre-selects Sales Ledger, Purchase Ledger and Cashbook. Banking pre-selects Cashbook and Bank Accounts. Payroll pre-selects Payroll. Excel starts with nothing assumed — you choose exactly what data each spreadsheet contains. All types remain available regardless of category — your system may hold more than the defaults suggest.",
				},
				{
					label: "Auto Mapped Connections",
					detail:
						'Access products (Financials, Dimensions Cloud, Paycircle, Plaid) are automatically field-mapped — no manual configuration required. The mapping screen shows "Auto Mapped" for each field.',
				},
				{
					label: "OAuth 2.0 Connections — Named Products",
					detail:
						'Xero, QuickBooks Online, and Sage Business Cloud connect via OAuth 2.0. You will need a Client ID and Client Secret from your developer app in each product\'s portal. Once both are entered, an "Authorise with [Product]" button appears — clicking this initiates the OAuth handshake and grants Cash Forecaster read access to your data. These are cloud-only connections — on-premise versions of these products are not supported.',
				},
				{
					label: "OAuth 2.0 Connections — Paycircle",
					detail:
						"Paycircle connects via OAuth 2.0 with bureau-level authentication. You will need your Client ID, Client Secret, and Bureau ID from your Paycircle account. Bureau-level credentials provide access to all companies within your bureau hierarchy — one set of credentials covers all companies. After connecting, you can select which company to pull into the forecast. Field mapping for Paycircle payroll fields is pre-populated automatically.",
				},
				{
					label: "OAuth 2.0 Connections — Generic Catchall",
					detail:
						'If your finance, payroll, or banking system supports OAuth 2.0 but is not in the named product list, select "Other finance system (OAuth 2.0)", "Other payroll system (OAuth 2.0)", or "Other bank feed provider (OAuth 2.0)". You will need your Client ID, Client Secret, and the Authorisation Endpoint URL from your provider\'s API documentation.',
				},
				{
					label: "Manual Field Mapping",
					detail:
						"For third-party systems (Xero, QuickBooks Online, Sage Business Cloud), you define field mappings in the Configure modal after connecting. For Excel sources, column mapping is completed inline during the Add New wizard — type a column letter (A, B, C) for each field, or use the preview table to click-to-assign. All required fields must be mapped before you can save an Excel source. Once all required fields are mapped, a validation step checks every row in your file for data issues before you are allowed to save.",
				},
				{
					label: "Excel — Format Declaration",
					detail:
						"Before mapping columns, the Excel wizard asks you to declare how your spreadsheet formats numbers and dates. Negative values must use one of three formats: minus sign (−1234.56), brackets ((1234.56)), or trailing minus (1234.56−). Colour-only and CR/DR notation are not accepted — number columns must contain plain numeric values only. Select the date format your file uses. Thousands separators, decimal separators, and currency symbols are handled automatically.",
				},
				{
					label: "Excel — Data Validation",
					detail:
						'Once all required column mappings are complete, a "Validate data" button appears. Clicking it scans every row in your file against the expected data types for each mapped field. Any rows with unexpected values — a text value in a date column, for example — are shown in a table with the row number, field, column, expected type, and actual value found. You can either go back and fix the file before re-uploading, or proceed excluding those rows. Invalid rows are logged but never imported.',
				},
				{
					label: "Excel — Mixed Column Splitter",
					detail:
						"If your spreadsheet stores incoming and outgoing values in a single column using positive and negative numbers, the wizard detects this automatically when you map the column. A prompt appears asking what a negative value represents in your data. Once you confirm, the system virtually splits the column — positive values flow to incoming, negative values (sign stripped) flow to outgoing.",
				},
				{
					label: "Excel — File Upload",
					detail:
						"Enter any file password first, then upload your spreadsheet. The upload validates the file in three stages — confirming it is readable, that column headers are present, and that data rows exist. The confirmation card shows the number of data rows and columns found from your actual file. Data is always read from sheet 1. You can re-upload a corrected version of the file at any time.",
				},
				{
					label: "Excel — File Password",
					detail:
						"If your spreadsheet is password-protected, enter the password before uploading. The password is entered first so the file can be opened and validated. Read-only access is all that is required — Cash Forecaster never writes back to your files.",
				},
				{
					label: "Other Finance / Payroll / Banking Systems",
					detail:
						'If your system is not in the named product list, you have two options. If it supports OAuth 2.0, select the matching "Other ... (OAuth 2.0)" option — you will be prompted for Client ID, Client Secret, and Authorisation Endpoint URL. If it does not support OAuth, select the plain "Other finance system", "Other payroll system", or "Other banking provider" option — you will be guided directly to Excel setup with a one-click redirect. Any system that can export to spreadsheet format can be connected via Excel.',
				},
				{
					label: "Sync Schedule",
					detail:
						'Configure how frequently each source refreshes its data. When setting up a new source, use "Save & Set Up Sync Schedule" in the Configure footer to move straight into the sync schedule setup without leaving the flow.',
				},
				{
					label: "Sync Now",
					detail:
						"Trigger an immediate sync for any connected source outside of its scheduled window.",
				},
				{
					label: "Sync History",
					detail:
						"View the full sync log for any source — timestamps, record counts, and any errors encountered.",
				},
				{
					label: "Multiple Instances",
					detail:
						"For Access Financials and Access Dimensions Cloud, you can connect multiple database instances — for example, multiple company entities within the same product. Already-connected instances are clearly labelled.",
				},
			],
			sectionsAndFields: [
				{
					name: "Source Cards",
					description:
						"One card per connected source — shows the source name, product type, connection status, last sync, record count, and sync schedule.",
				},
				{
					name: "Add New Data Source",
					description:
						"Choose category → choose product → complete connection details → save. You land directly in Configure — no intermediate confirmation screen.",
				},
				{
					name: "Connection Status Badge",
					description:
						'Green "Connected" means the last sync succeeded. An error state means the connection needs attention.',
				},
				{
					name: "Configure — Connection Tab",
					description:
						"Source name, type, and connection-level settings. For Excel sources configured via the wizard, this tab shows the uploaded file details and is read-only. For all other source types, connection credentials can be updated here.",
				},
				{
					name: "Configure — Mapping Tabs",
					description:
						'One tab per selected transaction type. Access products show "Auto Mapped" read-only. Third-party and Excel sources show editable field mapping. Excel tabs each have their own Worksheet Settings section (sheet name, header row, data start row) at the top of the tab.',
				},
				{
					name: "Mapping Progress Badges",
					description:
						"Each mapping tab shows a live progress badge — amber with a fraction while fields are being mapped, green with a tick when all required fields are complete.",
				},
				{
					name: "Click-to-Assign (Excel)",
					description:
						"In the file preview table, click any column header to open a field picker. Select the forecaster field to assign that column instantly. Already-mapped columns are highlighted.",
				},
				{
					name: "Field Search",
					description:
						"Search box within each mapping tab to quickly locate a specific field by name or description.",
				},
				{
					name: "Save & Set Up Sync Schedule",
					description:
						"In the Configure footer — saves configuration and opens the sync schedule modal immediately. If a schedule already exists, it opens in edit mode with current settings pre-populated.",
				},
			],
			useCases: [
				"Connecting Access Financials for the first time — Auto Mapped, activate in minutes, then set the sync schedule without leaving the flow.",
				"Adding a Plaid bank feed so real bank transaction data flows into the forecast automatically.",
				"Connecting Paycircle via OAuth 2.0 — entering Client ID, Client Secret, and Bureau ID, with payroll field mapping pre-populated automatically.",
				'Connecting a Xero instance via OAuth 2.0 — entering Client ID and Client Secret, then clicking "Authorise with Xero" to complete the OAuth handshake.',
				'Connecting a third-party payroll system that supports OAuth 2.0 but is not in the named product list — using the "Other payroll system (OAuth 2.0)" option with your own authorisation endpoint.',
				"Using the Excel catch-all for a system not in the product list — mapping different data types from different tabs of the same workbook, each with its own header row configuration.",
				"Importing a password-protected Excel export from a legacy finance system.",
				"Checking the sync history after a data discrepancy to identify whether a sync failure caused the issue.",
				"Adding a second Access Financials instance for a second company entity.",
			],
		},
		userGuide: {
			gettingStarted:
				"Click **Admin** then click **Data Sources**. All currently connected sources are listed as cards. To add a new source, click **Add New Data Source** — you are taken straight into Configure once the source is created.",
			steps: [
				{
					title: "How do I connect a new data source?",
					items: [
						{ text: "Click **Add New Data Source**." },
						{ text: "Enter a name in the **Data Source Name** field." },
						{
							text: "Click the appropriate source category tile: **Finance**, **Payroll**, **Banking**, or **Excel**.",
						},
						{
							text: "Select the specific product from the **Product** dropdown. If your product supports OAuth 2.0 but is not named, select the matching **Other ... (OAuth 2.0)** option. If your product has no integration, select the plain **Other** option — you will be guided to Excel setup.",
						},
						{
							text: "Complete the connection credentials for your product type.",
						},
						{
							text: "Review the transaction types — sensible defaults are pre-selected. Adjust as needed.",
						},
						{
							text: "Click **Add Data Source**.",
							note: "You are taken directly into the Configure screen — there is no intermediate confirmation. Complete field mapping and sync schedule in one flow without leaving the screen.",
						},
					],
				},
				{
					title: "How do I connect Paycircle?",
					items: [
						{
							text: "Click **Add New Data Source** and select **Payroll** as the source category.",
						},
						{ text: "Select **Paycircle** from the **Product** dropdown." },
						{
							text: "An amber banner confirms this is an OAuth 2.0 connection with bureau-level authentication.",
						},
						{
							text: "Enter your **Client ID**, **Client Secret**, and **Bureau ID** from your Paycircle account.",
						},
						{
							text: "Review the transaction types — **Payroll** is pre-selected.",
						},
						{
							text: "Click **Add Data Source**.",
							note: "Bureau-level credentials cover all companies in your hierarchy. After connecting you can select which company to pull into the forecast. Field mapping for Paycircle payroll fields is pre-populated automatically.",
						},
					],
				},
				{
					title: "How do I connect Xero, QuickBooks Online, or Sage?",
					items: [
						{
							text: "Click **Add New Data Source** and select **Finance** as the source category.",
						},
						{
							text: "Select **Xero**, **QuickBooks Online**, or **Sage Business Cloud** from the **Product** dropdown.",
						},
						{
							text: "An amber banner confirms this is a cloud-only OAuth 2.0 connection. On-premise versions of these products are not supported.",
						},
						{
							text: "Enter your **Client ID** and **Client Secret** from the developer app registered in the product's developer portal.",
						},
						{
							text: "Once both fields are filled, click the **Authorise with [Product]** button that appears to complete the OAuth handshake.",
						},
						{
							text: "Select the transaction types and click **Add Data Source**.",
						},
						{
							text: "Field mapping is manual — configure it in the Configure screen after the source is created.",
							note: "OAuth tokens can expire. If your connection shows an error after previously working, return to the Connection tab in Configure and re-authorise using the same Client ID and Client Secret.",
						},
					],
				},
				{
					title: "How do I connect a third-party system via OAuth 2.0?",
					items: [
						{
							text: "Click **Add New Data Source** and select the appropriate category (**Finance**, **Payroll**, or **Banking**).",
						},
						{
							text: "Select the matching **Other ... (OAuth 2.0)** option from the **Product** dropdown.",
						},
						{
							text: "An amber banner confirms this is a generic OAuth 2.0 connection.",
						},
						{
							text: "Enter your **Client ID**, **Client Secret**, and **Authorisation Endpoint URL** from your provider's API documentation.",
						},
						{
							text: "Select the transaction types and click **Add Data Source**.",
						},
						{
							text: "Field mapping is manual — configure it in the Configure screen after the source is created.",
							note: "If your system does not support OAuth 2.0 at all, use the plain **Other finance system**, **Other payroll system**, or **Other banking provider** option instead — this routes you directly to Excel setup.",
						},
					],
				},
				{
					title: "How do I connect an Excel data source?",
					items: [
						{
							text: "Click **Add New Data Source** and select **Excel** as the source category.",
						},
						{
							text: "Select your product or choose **Other** if your system is not listed.",
						},
						{
							text: "If your file is password-protected, enter the **File Password** before uploading. Leave blank if not protected.",
						},
						{
							text: "Click **Choose File** or drag and drop your spreadsheet onto the upload zone. Accepted formats: .xlsx, .xls, .csv. Maximum file size: 50MB. The file is validated in three stages — the confirmation card shows the actual row and column count from your file.",
						},
						{
							text: "On the **Format Declaration** step, select how negative values are shown in your file (minus sign, brackets, or trailing minus) and the date format used. Thousands separators and currency symbols are handled automatically.",
						},
						{
							text: "Select the **transaction type** this spreadsheet contains — one type per Excel source.",
						},
						{
							text: "On the **Column Mapping** step, type the column letter (A, B, C) for each field the forecaster needs, or collapse the preview table and use the column letters shown in the header row.",
						},
						{
							text: "Once all required fields are mapped, click **Validate data**. Every row in your file is checked — any rows with unexpected values are listed with the row number, field, column, and what was found.",
						},
						{
							text: "If validation finds issues, either click **Back and fix file** to upload a corrected version, or proceed — invalid rows are excluded from the import.",
							note: "If validation passes cleanly, the Save button unlocks. All required fields must be mapped and validation must pass before you can save.",
						},
					],
				},
				{
					title: "How do I configure field mapping?",
					items: [
						{
							text: "In Configure, click the mapping tab for the transaction type you want to set up.",
						},
						{
							text: "For Access products — all fields show **Auto Mapped**. No action is required.",
						},
						{
							text: 'For Excel sources — use the file preview table at the top. Click a column header to open the field picker, then select the forecaster field that column contains. Use the expand/collapse toggle on the "File Preview" heading to show or hide the table while mapping.',
						},
						{
							text: "For third-party sources — enter or confirm the source field names in each row.",
						},
						{
							text: "Watch the progress badge on each tab — it turns green with a tick when all required fields are mapped.",
							note: "Data is always read from the first sheet. Row 1 is the header row; data begins on row 2. The Date Format setting at the bottom of each mapping tab can be adjusted per data type if needed.",
						},
					],
				},
				{
					title: "How do I set up a sync schedule?",
					items: [
						{
							text: "In the Configure footer, click **Save & Set Up Sync Schedule** instead of **Save Changes**.",
						},
						{ text: "The sync schedule panel opens immediately." },
						{
							text: "Set the frequency, days, and time window, then click **Save**.",
							note: "If a schedule already exists for this source, it opens pre-populated in edit mode. Clicking **Save Changes** closes Configure without opening the schedule panel — use this when you only need to update mapping.",
						},
					],
				},
				{
					title: "How do I trigger a manual sync?",
					items: [
						{ text: "Find the source card you want to sync." },
						{ text: "Click **Sync Now**." },
						{
							text: "The sync runs immediately. The last sync time and record count update on the card.",
							note: "Manual syncs do not affect the configured schedule — the next scheduled sync runs at its normal time.",
						},
					],
				},
				{
					title: "How do I investigate a sync error?",
					items: [
						{ text: "Click **Sync History** on the affected source card." },
						{ text: "Review the log entries for error messages." },
						{
							text: "Click **Configure** to check the connection credentials and field mapping.",
						},
						{
							text: "Once corrected, click **Sync Now** to verify the connection is restored.",
							note: "A failed sync means the forecast may not reflect the most recent data. Resolve sync errors promptly to maintain forecast accuracy.",
						},
					],
				},
			],
			proTips: [
				"Connect Access products first — they are Auto Mapped and activate in minutes. Third-party and Excel connections require more preparation.",
				'Use "Save & Set Up Sync Schedule" when setting up a new source so the entire flow — connection, mapping, and schedule — is complete before you leave the screen.',
				"For Excel sources with multiple data types, configure each mapping tab's Worksheet Settings independently. Do not assume the same sheet name or header row applies to all tabs.",
				"If you choose Upload File, the sheet name detected during validation is pre-populated in each mapping tab's Worksheet Settings — check it is correct before saving, especially if the file has multiple sheets.",
				'If your system is not in the product list, do not cancel and restart — select "Other finance system", "Other payroll system", or "Other banking provider" and use the one-click redirect to Excel setup. You keep all your progress.',
				"If your Excel file is password-protected, enter the file password on the Connection tab. You only need read access — Cash Forecaster never modifies your files.",
				"The transaction type defaults are a starting point — if your system holds data types beyond the defaults (e.g. a finance system that also handles payroll), tick those types too.",
				"Review sync history weekly — a source that silently fails can cause forecast drift before anyone notices.",
				"When connecting multiple instances of Access Financials (e.g. two company entities), give each a clear descriptive name so they are easy to identify on the source list.",
				"For third-party connections, have your API credentials ready before starting — the session may time out if left idle.",
			],
		},
		faq: [
			{
				question: "Which products are Auto Mapped?",
				answer:
					"Access Financials, Access Dimensions Cloud, Paycircle, and Plaid are all Auto Mapped — field mapping is handled automatically because we own or have deep knowledge of those products. Third-party products (Xero, QuickBooks Online, Sage Business Cloud) and Excel sources require manual field mapping.",
			},
			{
				question: "My finance system is not in the product list. What do I do?",
				answer:
					'First check whether your system supports OAuth 2.0 — if it does, select "Other finance system (OAuth 2.0)" and enter your Client ID, Client Secret, and Authorisation Endpoint URL from your provider\'s API documentation. If it does not support OAuth, select the plain "Other finance system" option — you will be guided directly to Excel setup with a one-click redirect. Excel works for any system that can export to spreadsheet format, including Sage 50, Sage 200, legacy finance systems, and any other unlisted product.',
			},
			{
				question: "What credentials do I need for a Paycircle connection?",
				answer:
					"You need three things: a Client ID, a Client Secret, and your Bureau ID — all available from your Paycircle account settings. Bureau-level credentials provide access to all companies within your bureau hierarchy. One set of credentials covers all companies — you do not need separate credentials per company.",
			},
			{
				question:
					"What credentials do I need for a Xero, QuickBooks Online, or Sage connection?",
				answer:
					'You need a Client ID and Client Secret from a developer app you register in the product\'s developer portal (Xero Developer, QuickBooks Developer, or Sage Developer). Once both are entered in Cash Forecaster, an "Authorise with [Product]" button appears — clicking it completes the OAuth handshake. Note: these connections are cloud-only — on-premise versions of these products are not supported.',
			},
			{
				question: "Can I upload a file instead of providing a file path?",
				answer:
					'Yes. When setting up an Excel data source, select "Upload File" as the file access method. You can drag and drop or click to browse. The system validates the file automatically — checking it is readable, has column headers, and contains data rows — then confirms the sheet details. Accepted formats are .xlsx, .xls, and .csv up to 50MB. You can replace the uploaded file at any time from the connection settings.',
			},
			{
				question: "Can I have multiple data types from one Excel file?",
				answer:
					"Yes — that is exactly what Excel is designed for. Enable multiple transaction types when setting up the source, then configure each mapping tab independently. Each tab has its own Worksheet Settings so you can point Sales Orders at one sheet and Purchase Orders at another, with different header row configurations if needed.",
			},
			{
				question:
					"My Excel file is password-protected. Can I still connect it?",
				answer:
					"Yes. Enter the file password in the File Password field on the Connection tab. Cash Forecaster only needs read access to extract data — it never writes back to your files. Sheet-level and cell-level protection do not require a password for read access.",
			},
			{
				question: "Can I connect the same product more than once?",
				answer:
					"Yes, for Access Financials and Access Dimensions Cloud you can connect multiple instances — for example, one per company entity. The instance picker shows which instances are already connected and prevents duplicate connections. For other products, add a separate data source entry for each connection.",
			},
			{
				question:
					"What is the difference between a named product, an OAuth catchall, and Excel?",
				answer:
					'Named products (Xero, QuickBooks Online, Sage Business Cloud, Paycircle) connect via OAuth 2.0 with guided setup. The "Other ... (OAuth 2.0)" options are for unlisted products that still support OAuth 2.0 — you supply your own Client ID, Client Secret, and Authorisation Endpoint URL. The plain "Other" options route you to Excel setup, which works for any system that can export to spreadsheet format but requires a manual export each time (or a watched folder for automation).',
			},
			{
				question: "How often does data sync?",
				answer:
					'Each source has its own configurable sync schedule. Use "Save & Set Up Sync Schedule" in the Configure footer to set this up as part of the initial connection flow. You can also adjust the schedule at any time via Configure, or trigger an immediate sync using Sync Now.',
			},
			{
				question:
					"Why are some transaction types pre-ticked when I add a new source?",
				answer:
					"The system pre-selects the transaction types most commonly found in each source category — Finance gets Sales Ledger, Purchase Ledger and Cashbook; Banking gets Cashbook and Bank Accounts; Payroll gets Payroll; Excel starts blank. These are defaults only — your system may support additional types, and you can tick or untick any option before saving.",
			},
		],
		relatedPages: [
			{ label: "Parent Child Categories", pageKey: "admin-category-mapping" },
			{ label: "General Settings", pageKey: "admin-general-settings" },
			{ label: "Admin Dashboard", pageKey: "admin-dashboard" },
		],
	},

	// ── ONBOARDING ASSISTANT ──────────────────────────────────────
	"onboarding-assistant": {
		pageTitle: "Onboarding Assistant",
		description:
			"A guided setup flow that walks System Administrators through the key configuration steps before the team starts using Cash Forecaster.",
		icon: "Rocket",
		iconColor: "bg-emerald-500",
		functionality: {
			overview:
				"The Onboarding Assistant appears automatically the first time a System Administrator logs in. It guides you through five configuration steps — General Settings, Data Sources, Roles & Permissions, User Management, and Dashboard Layout — so the product is properly set up before your finance team arrives. Each step embeds the real settings page, so any changes you make are immediately saved. You can skip steps and return to them later, and the assistant remembers where you left off even after a page refresh.",
			keyFeatures: [
				{
					label: "Full-Screen Guided Flow",
					detail:
						"The assistant replaces the app shell entirely so there are no distractions — just the setup steps in order, with clear progress indicators throughout.",
				},
				{
					label: "Real Settings Pages Embedded",
					detail:
						"Each step shows the actual settings page from the app — not a simplified copy. Any changes you make inside the assistant are immediately persisted, exactly as if you had gone to Settings directly.",
				},
				{
					label: "Skippable Steps",
					detail:
						"Every step can be skipped and completed later. The assistant never blocks you from proceeding — it guides without forcing.",
				},
				{
					label: "Progress Saved Automatically",
					detail:
						"Your progress is saved to your browser between sessions. If you close the tab mid-setup, the assistant will reopen at the step you left off.",
				},
				{
					label: "Completion Screen",
					detail:
						"The final screen shows a summary of all five steps with their status — green for completed, amber for skipped. Click Confirm Onboarding Complete to finish and go to the Admin Dashboard.",
				},
				{
					label: "Restart Onboarding",
					detail:
						"A Restart Onboarding item in the sidebar lets you reopen the assistant at any time — even after you have fully completed setup.",
				},
			],
			sectionsAndFields: [
				{
					name: "Welcome Screen",
					description:
						'Personalised greeting with a preview of the five setup steps. Click "Let\'s Start" to begin, or "Restart Onboarding" to dismiss and return to the app.',
				},
				{
					name: "Step 1 — General Settings",
					description:
						"Configure your company name, financial year, default currency, and forecast settings. Recommended to complete before your team starts using the product.",
				},
				{
					name: "Step 2 — Data Sources",
					description:
						"Connect your accounting, payroll, and banking systems so the forecast has real data to work with. The more sources you connect, the more accurate your cash position.",
				},
				{
					name: "Step 3 — Roles & Permissions",
					description:
						"Review and configure the roles available in the system and the permissions each role holds. Define your roles before assigning them to users in Step 4.",
				},
				{
					name: "Step 4 — User Management",
					description:
						"Invite your finance team and assign roles. Role changes are staged and validated before saving — the system prevents you from accidentally removing the last administrator.",
				},
				{
					name: "Step 5 — Dashboard Layout",
					description:
						"Configure the default card layout for each role. Optional — you can set this up later from Settings → Dashboard Layout.",
				},
				{
					name: "Completion Screen",
					description:
						"A summary of all five steps showing their status (completed or skipped). Click Confirm Onboarding Complete to finish — you will be taken to the Admin Dashboard and a summary of any skipped steps will appear.",
				},
			],
			useCases: [
				"Setting up Cash Forecaster for the first time before inviting the finance team.",
				"Configuring roles and permissions before assigning users — so the right access levels are in place from the start.",
				"Returning to the assistant after initial setup to connect additional data sources.",
				"Using the completion screen Edit links to jump directly to a specific settings screen.",
				"Restarting the assistant after onboarding a new entity or reconfiguring the system.",
			],
		},
		userGuide: {
			gettingStarted:
				"The Onboarding Assistant appears automatically when you first log in as a System Administrator. If you have dismissed it or completed it previously, click **Restart Onboarding** in the System Settings section of the left sidebar to reopen it.",
			steps: [
				{
					title: "How do I navigate between steps?",
					items: [
						{ text: "Click **Next →** to advance to the next step." },
						{ text: "Click **← Back** to return to the previous step." },
						{
							text: "Click **Skip for now** to skip the current step and move forward without completing it.",
							note: "Skipped steps are shown in amber on the completion screen. You can complete them later from the completion screen or by navigating to the relevant settings screen directly.",
						},
						{
							text: "The progress sidebar on the left shows your status — upcoming steps are grey, the active step is highlighted, and completed steps show a green tick.",
						},
					],
				},
				{
					title: "How do I save changes in the User Management step?",
					items: [
						{
							text: "In the User Management step, click the **Forecaster Role** dropdown on a user row to stage a role change.",
						},
						{
							text: "An amber indicator appears on the row and a pending changes banner appears at the top of the list.",
						},
						{
							text: "Click **Save** in the pending changes banner to apply all staged changes.",
							note: "The system validates that at least one user will retain administrator access before saving. If saving would remove all administrators, the save is blocked and an explanation is shown.",
						},
						{
							text: "Click **Discard** in the banner to cancel all staged changes without applying them.",
						},
					],
				},
				{
					title: "How do I complete the assistant?",
					items: [
						{
							text: "Complete or skip all five steps to reach the completion screen.",
						},
						{
							text: "Review the status of each step — green for complete, amber for skipped.",
						},
						{ text: "Click **Confirm Onboarding Complete →**." },
						{
							text: "You are taken to the Admin Dashboard. A toast message lists any steps that were skipped so you can follow up on them later.",
							note: "Clicking an Edit link on the completion screen closes the assistant and opens that settings screen directly in the main app. Use Restart Onboarding from the sidebar to return to the guided flow.",
						},
					],
				},
				{
					title: "How do I dismiss the assistant and return to the app?",
					items: [
						{
							text: "Click **Restart Onboarding** in the top-right corner of the assistant.",
							note: "Your progress is saved automatically. When you reopen the assistant it returns to the step you were on.",
						},
					],
				},
				{
					title: "How do I reopen the assistant after completing it?",
					items: [
						{
							text: "Click **Restart Onboarding** in the System Settings section of the left sidebar.",
						},
						{
							text: "A confirmation dialog appears — confirm to reopen the assistant from the Welcome screen.",
						},
					],
					requiredPermission: "manage_roles_permissions",
				},
			],
			proTips: [
				"Complete General Settings and Data Sources first — these have the biggest impact on the accuracy of the forecast from day one.",
				"Configure Roles & Permissions before User Management — defining your roles first means you can assign the right access level as each user is invited.",
				"The User Management step uses a draft-save pattern — stage your role changes first, then click Save in the pending changes banner. Nothing is applied until you save.",
				'The assistant embeds the real settings screens — any changes you make inside the assistant are live immediately. There is no separate "save" step (except for the User Management draft pattern).',
				"Use the completion screen Edit links after setup to quickly jump to specific settings without hunting through the sidebar.",
			],
		},
		faq: [
			{
				question: "Who can see the Onboarding Assistant?",
				answer:
					"Only users who hold both the System Administrator forecaster role and the Administrator Evo role see the Onboarding Assistant. Finance Managers, CFOs, and other roles do not see it.",
				requiredPermission: "manage_roles_permissions",
			},
			{
				question: "What happens if I skip all the steps?",
				answer:
					"You can skip every step and go straight to the completion screen. Skipped steps are shown in amber. You can use the Edit links on the completion screen, or navigate to Settings directly, to complete any configuration at any time.",
			},
			{
				question: "Does the assistant reset if I refresh the page?",
				answer:
					"No — your progress is saved to your browser storage keyed to your user account. Refreshing the screen will reopen the assistant at the step you were on.",
			},
			{
				question: "Can I run through the assistant again after completing it?",
				answer:
					"Yes — click Restart Onboarding in the System Settings section of the sidebar. A confirmation prompt appears before the assistant reopens.",
				requiredPermission: "manage_roles_permissions",
			},
			{
				question:
					"Why does the User Management step have a Save button but other steps do not?",
				answer:
					"Role assignments are sensitive changes — accidentally saving the wrong role could lock users out of the system. The draft-save pattern lets you stage multiple role changes and review them before committing. All other steps save immediately because those settings carry lower risk.",
			},
			{
				question:
					'What is the difference between "Skip for now" and "Restart Onboarding"?',
				answer:
					'"Skip for now" skips the current step and moves to the next one — you stay in the assistant. "Restart Onboarding" (top-right corner) closes the assistant entirely and takes you to the app. You can reopen it at any time from the sidebar.',
			},
		],
		relatedPages: [
			{ label: "General Settings", pageKey: "admin-general-settings" },
			{ label: "Data Sources", pageKey: "admin-data-sources" },
			{ label: "User Management", pageKey: "admin-users" },
			{ label: "Dashboard Layout", pageKey: "admin-dashboard-layout" },
			{ label: "Admin Dashboard", pageKey: "admin-dashboard" },
		],
	},

	// ── REFRESH FORECAST ────────────────────────────────────────────────────────
	"forecast-refresh": {
		pageTitle: "Refresh Forecast",
		description:
			"Manually trigger a full forecast recalculation outside of the scheduled sync.",
		icon: "RefreshCw",
		iconColor: "bg-teal-500",
		functionality: {
			overview:
				"The Refresh Forecast page lets you manually trigger a full forecast recalculation outside of the automatically scheduled sync. This is an exception tool — it should be used sparingly, when you need the forecast to reflect new data immediately rather than waiting for the next scheduled run.",
			keyFeatures: [
				{
					label: "Current Status",
					detail:
						"Shows when the last refresh ran, how long it took, who triggered it, and how old the current forecast is.",
				},
				{
					label: "What to Expect",
					detail:
						"Explains what happens during a refresh, what is temporarily locked, and how long it will take — so you know what to expect before you start.",
				},
				{
					label: "Acknowledgement Checkbox",
					detail:
						"Before starting, you confirm that you understand recurring transactions, data source configuration, and scenario modifications will be temporarily locked. The Start button stays inactive until you tick this.",
				},
				{
					label: "Refresh History",
					detail:
						"A table of recent refresh runs showing date, type (Scheduled or Manual), who triggered it, duration, and any notes — for example, data source warnings.",
				},
			],
			sectionsAndFields: [
				{
					name: "Current Status",
					description:
						"Four fields: last refresh date/time and how long ago, duration of the last run, who triggered it, and how old the current forecast is.",
				},
				{
					name: "What to Expect",
					description:
						"Estimated duration (based on last refresh), what happens during the refresh, what gets locked, and a reminder to check the schedule if you are running manual refreshes regularly.",
				},
				{
					name: "Start Refresh",
					description:
						"The acknowledgement checkbox and Start Forecast Refresh button. Checkbox must be ticked before the button activates.",
				},
				{
					name: "Refresh History",
					description:
						"A table of the most recent refresh runs — most recent at top — with Date/Time, Type, Triggered By, Duration, and Notes columns.",
				},
			],
			useCases: [
				"You have just reconnected a data source that was temporarily disconnected and want the forecast to immediately reflect the recovered data.",
				"A data source sync pulled in a large batch of transactions and you need the forecast updated before a board meeting in an hour.",
				"You want to check whether a recently committed scenario is visible in the current live forecast.",
			],
		},
		userGuide: {
			gettingStarted:
				"Select **Refresh Forecast** from the Forecasts section of the left-hand navigation. The page shows the current forecast status and the history of recent refreshes. Read the **What to Expect** section before triggering a refresh for the first time.",
			steps: [
				{
					title: "How do I start a forecast refresh?",
					items: [
						{
							text: "Review the **Current Status** section to confirm you need a manual refresh — check how old the current forecast is.",
						},
						{
							text: "Read the **What to Expect** section to understand what will be locked during the refresh.",
						},
						{
							text: 'Tick the acknowledgement checkbox: "I understand this will temporarily lock recurring transactions, data source configuration and scenario modifications while the refresh runs."',
						},
						{ text: "Click **Start Forecast Refresh**." },
						{
							text: "A progress indicator appears. The refresh runs in the background.",
							note: "Do not navigate away — the refresh continues, but the progress display is only shown on this page.",
						},
						{
							text: "When complete, a green confirmation message appears and the Refresh History table updates with the new entry.",
						},
					],
				},
				{
					title: "What is locked during a refresh?",
					items: [
						{
							text: "Recurring transactions become read-only — you cannot add, edit, or delete them.",
						},
						{
							text: "Data source configuration is locked — you cannot connect, disconnect, or reconfigure data sources.",
						},
						{
							text: "Scenario activation and modification are locked — you cannot activate, park, or edit scenarios.",
						},
						{
							text: "Everything else continues normally — actions, comments, preferences, reports, and admin configuration are unaffected.",
							note: "The lock is temporary and lifts automatically when the refresh completes.",
						},
					],
				},
				{
					title: "How do I check the refresh history?",
					items: [
						{
							text: "Scroll to the **Refresh History** section at the bottom of the page.",
						},
						{
							text: "The most recent refresh is shown at the top of the table.",
						},
						{
							text: "Any warnings from a refresh run — such as a data source returning incomplete data — appear in the Notes column.",
						},
					],
				},
			],
			proTips: [
				"If you find yourself triggering manual refreshes regularly, the scheduled refresh frequency is probably too low. Go to Admin → General Settings to increase the frequency.",
				"The estimated duration shown is based on the last refresh run, with a small random variance. For accurate planning, treat it as an approximate guide rather than a guarantee.",
				"The Refresh History table records every run — both scheduled and manual — so you have a full audit trail of when the forecast was last updated and by whom.",
			],
		},
		faq: [
			{
				question: "Who can trigger a manual forecast refresh?",
				answer:
					"Only users with the Trigger forecast refresh permission can access this page. By default, this is Finance Managers and CFOs.",
			},
			{
				question: "What happens if a refresh is already running?",
				answer:
					"The Start button is disabled while a refresh is in progress. Wait for the current refresh to complete before triggering another.",
			},
			{
				question: "Will a manual refresh affect the scheduled refreshes?",
				answer:
					"No. The scheduled refresh will run at its next configured time regardless of any manual refreshes you trigger.",
			},
			{
				question: "How do I adjust the scheduled refresh frequency?",
				answer:
					"Go to Admin → General Settings and scroll to the Forecast Refresh Schedule section. You can configure the frequency (every 6 hours, every 12 hours, or daily), the days of the week, and the operating window.",
				requiredPermission: "manage_general_settings",
			},
		],
		relatedPages: [
			{ label: "General Settings", pageKey: "admin-general-settings" },
			{ label: "90-Day Forecast", pageKey: "forecast-90" },
			{ label: "12-Month Forecast", pageKey: "forecast-12m" },
			{ label: "3-Year Forecast", pageKey: "forecast-3y" },
		],
	},
};
